import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValueEvent, useSpring } from "framer-motion";
import {
  ArrowRight, AlertTriangle, HeartHandshake, Footprints, FileLock2, QrCode,
  Calendar, MapPin, Shield, ChevronDown, Award, Sparkles, Heart, PawPrint,
  Briefcase, Bell, X, Zap, Users, Flag, ShieldCheck, Star, TrendingUp,
  ChevronRight, CheckCircle2, Home, Thermometer, Radar, Download,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGO_WHITE } from "@/lib/assets";
import { trackInstallEvent } from "@/lib/installTrack";
import IlloImg from "@/components/IlloImg";
import InstallPrompt, { InstallInstructionsModal } from "@/components/InstallPrompt";
import PlanComparison from "@/components/PlanComparison";
import VixelMark from "@/components/VixelMark";
import StickyInstallBar from "@/components/StickyInstallBar";
import LandingHero from "@/components/landing/LandingHero";
import { useInstall } from "@/context/InstallContext";

const COLORS = {
  emergency: "#FF3B30",
  missions: "#FFB020",
  match: "#34D399",
};

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // PWA: when launched as installed app, show a quick welcome card instead of the landing marketing.
  const isStandalone = (typeof window !== "undefined") && (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: fullscreen)").matches ||
    window.matchMedia?.("(display-mode: minimal-ui)").matches ||
    window.navigator.standalone === true ||
    (typeof document !== "undefined" && document.referrer.startsWith("android-app://"))
  );

  // If not logged in and standalone, redirect to login
  // If logged in and standalone, send straight to the main app home (Welcome3Cards removed)
  useEffect(() => {
    if (isStandalone && !user) {
      navigate("/login", { replace: true });
    } else if (isStandalone && user) {
      navigate("/app", { replace: true });
    }
  }, [isStandalone, user, navigate]);

  if (isStandalone && user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FBF7F1] text-[#1A1A1A] relative orio-grain">
      {/* Ambient colored light fields — orange, peach, lime, sky and rose blurred glows */}
      <AmbientLights />
      <ProgressBar />
      <TopNav />
      <LandingHero />
      <ReviewsRotator />
      <StoryScroll />
      <MapLiveBlock />
      <IdentityBlock />
      <FeatureGallery />
      <PricingBlock />
      <InstallAppBlock />
      <FAQBlock />
      <BottomAura />
      <Footer />
      <StickyInstallBar />
    </div>
  );
}

/* ---------- AMBIENT LIGHT FIELD (warm theme, multi-color glows) ---------- */
function AmbientLights() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {/* Orange aurora — kicks in AFTER hero+reviews, around mission section */}
      <motion.div
        className="absolute top-[140vh] right-[-12%] w-[85vw] h-[85vw] max-w-[1300px] max-h-[1300px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(255,122,26,0.50) 0%, rgba(255,122,26,0) 60%)",
          filter: "blur(70px)",
        }}
        animate={{ x: [0, -40, 25, 0], y: [0, 50, 25, 0], scale: [1, 1.10, 1.04, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Peach aurora — story scroll section */}
      <motion.div
        className="absolute top-[170vh] left-[-15%] w-[80vw] h-[80vw] max-w-[1200px] max-h-[1200px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(244,184,60,0.45) 0%, rgba(244,184,60,0) 60%)",
          filter: "blur(70px)",
        }}
        animate={{ x: [0, 60, 25, 0], y: [0, -40, 50, 0], scale: [1, 1.12, 1.05, 1] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />
      {/* Lime aurora — middle of story scroll */}
      <motion.div
        className="absolute top-[210vh] left-[25%] w-[75vw] h-[75vw] max-w-[1100px] max-h-[1100px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(45,186,96,0.35) 0%, rgba(45,186,96,0) 60%)",
          filter: "blur(70px)",
        }}
        animate={{ x: [0, -50, 35, 0], y: [0, 35, -25, 0], scale: [1, 1.14, 1.06, 1] }}
        transition={{ duration: 30, repeat: Infinity, ease: "easeInOut", delay: 5 }}
      />
      {/* Rose / red aurora — after story */}
      <motion.div
        className="absolute top-[260vh] right-[-12%] w-[80vw] h-[80vw] max-w-[1200px] max-h-[1200px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(255,59,48,0.32) 0%, rgba(255,59,48,0) 60%)",
          filter: "blur(80px)",
        }}
        animate={{ x: [0, -45, -15, 0], y: [0, -50, 25, 0], scale: [1, 1.10, 1.04, 1] }}
        transition={{ duration: 32, repeat: Infinity, ease: "easeInOut", delay: 7 }}
      />
      {/* Sky aurora — map section */}
      <motion.div
        className="absolute top-[320vh] left-[-10%] w-[80vw] h-[80vw] max-w-[1200px] max-h-[1200px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(91,158,224,0.36) 0%, rgba(91,158,224,0) 60%)",
          filter: "blur(80px)",
        }}
        animate={{ x: [0, 50, 20, 0], y: [0, 40, -10, 0], scale: [1, 1.10, 1.05, 1] }}
        transition={{ duration: 34, repeat: Infinity, ease: "easeInOut", delay: 9 }}
      />
      {/* Violet aurora — identity section */}
      <motion.div
        className="absolute top-[400vh] right-[20%] w-[70vw] h-[70vw] max-w-[1000px] max-h-[1000px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(139,92,246,0.28) 0%, rgba(139,92,246,0) 60%)",
          filter: "blur(80px)",
        }}
        animate={{ x: [0, -40, 20, 0], y: [0, -30, 20, 0], scale: [1, 1.10, 1.06, 1] }}
        transition={{ duration: 36, repeat: Infinity, ease: "easeInOut", delay: 11 }}
      />
    </div>
  );
}

/* ---------- BOTTOM AURA (animated brand glow rising from footer) ---------- */
function BottomAura() {
  return (
    <div aria-hidden className="pointer-events-none absolute left-0 right-0 bottom-0 z-0 overflow-hidden" style={{ height: "120vh" }}>
      {/* Three drifting brand-colored radials that breathe + drift on a slow loop */}
      <motion.div
        className="absolute bottom-[-30%] left-[-10%] w-[80vw] h-[80vw] max-w-[1100px] max-h-[1100px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(255,59,48,0.42) 0%, rgba(255,59,48,0) 65%)",
          filter: "blur(90px) saturate(1.2)",
          willChange: "transform",
          transform: "translate3d(0,0,0)",
        }}
        animate={{ x: [0, 60, 30, 0], y: [0, -40, -20, 0], scale: [1, 1.08, 1.04, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-[-25%] left-[20%] w-[80vw] h-[80vw] max-w-[1100px] max-h-[1100px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(255,176,32,0.46) 0%, rgba(255,176,32,0) 65%)",
          filter: "blur(90px) saturate(1.2)",
          willChange: "transform",
          transform: "translate3d(0,0,0)",
        }}
        animate={{ x: [0, -50, 30, 0], y: [0, -30, -50, 0], scale: [1, 1.10, 1.05, 1] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
      <motion.div
        className="absolute bottom-[-30%] right-[-10%] w-[80vw] h-[80vw] max-w-[1100px] max-h-[1100px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(52,211,153,0.40) 0%, rgba(52,211,153,0) 65%)",
          filter: "blur(90px) saturate(1.2)",
          willChange: "transform",
          transform: "translate3d(0,0,0)",
        }}
        animate={{ x: [0, -40, -20, 0], y: [0, -50, -10, 0], scale: [1, 1.12, 1.06, 1] }}
        transition={{ duration: 30, repeat: Infinity, ease: "easeInOut", delay: 4 }}
      />
      {/* Soft amber crown right at the footer base */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/3"
        style={{
          background:
            "radial-gradient(50% 60% at 50% 100%, rgba(255,176,32,0.22) 0%, rgba(0,0,0,0) 70%)",
        }}
      />
    </div>
  );
}

/* ---------- PROGRESS BAR ---------- */
function ProgressBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 25, mass: 0.4 });
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-[2px] z-[60] origin-left orio-grad-bg"
      style={{ scaleX }}
    />
  );
}

/* ---------- TOP NAV (sticky CTA always visible) ---------- */
function TopNav() {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className="fixed top-2 left-0 right-0 z-50 px-3 sm:px-6">
      <div
        className={`relative max-w-7xl mx-auto px-4 sm:px-5 py-3 flex items-center justify-between rounded-full overflow-hidden transition-all duration-500`}
        style={{
          background: scrolled
            ? "rgba(251,247,241,0.55)"
            : "rgba(255,255,255,0.45)",
          backdropFilter: "blur(22px) saturate(180%)",
          WebkitBackdropFilter: "blur(22px) saturate(180%)",
          border: scrolled ? "1px solid rgba(240,235,227,0.6)" : "1px solid rgba(255,255,255,0.4)",
          boxShadow: scrolled
            ? "0 8px 30px -10px rgba(0,0,0,0.08)"
            : "0 14px 40px -10px rgba(255,122,26,0.20), 0 6px 20px -6px rgba(45,186,96,0.14)",
        }}
      >
        {/* Animated ORIO gradient layer visible at the TOP of the page (fades away when scrolled) */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none transition-opacity duration-500"
          style={{
            background:
              "linear-gradient(120deg, rgba(255,59,48,0.18) 0%, rgba(255,122,26,0.20) 25%, rgba(255,176,32,0.20) 50%, rgba(45,186,96,0.18) 75%, rgba(91,158,224,0.18) 100%)",
            backgroundSize: "300% 300%",
            animation: "orio-bg-shift 12s ease infinite",
            opacity: scrolled ? 0 : 1,
          }}
        />
        <Link to="/" data-testid="landing-logo" className="relative z-10 flex items-center -my-1">
          <div
            aria-label="ORIO"
            className="h-7 sm:h-8 w-[72px] sm:w-[84px]"
            style={{
              background: "linear-gradient(120deg, #ff3b30 0%, #ffb020 50%, #34d399 100%)",
              backgroundSize: "200% 200%",
              animation: "orio-bg-shift 6s ease infinite",
              WebkitMask: `url(${LOGO_WHITE}) no-repeat center / contain`,
              mask: `url(${LOGO_WHITE}) no-repeat center / contain`,
            }}
          />
        </Link>
        <nav className="relative z-10 hidden md:flex items-center gap-7 text-sm text-[#3D3D3D] font-medium">
          <a href="#story" className="hover:text-[#FF7A1A] transition-colors">Come funziona</a>
          <a href="#cassetto" className="hover:text-[#FF7A1A] transition-colors">Cassetto</a>
          <a href="#faq" className="hover:text-[#FF7A1A] transition-colors">FAQ</a>
          <a href="#prezzo" className="hover:text-[#FF7A1A] transition-colors">Prezzo</a>
        </nav>
        <div className="relative z-10 flex items-center gap-1.5">
          {user ? (
            <>
              <span
                data-testid="landing-user-pill"
                className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-white/60 text-xs text-[#3D3D3D]"
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{
                  background: user?.is_subscribed ? "#34D399" : user?.in_trial ? "#FFB020" : "#9aa0a6",
                }} />
                Ciao, {user.name?.split(" ")[0] || "tu"}
              </span>
              <Link
                to="/app"
                data-testid="landing-goto-app"
                className="orio-cta-btn relative px-4 py-2 rounded-full text-sm font-semibold text-black overflow-hidden inline-flex items-center gap-1.5"
              >
                <span className="relative z-10 inline-flex items-center gap-1.5">
                  Vai all&apos;app
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="landing-login-btn"
                className="hidden sm:inline-flex px-3.5 py-2 rounded-full text-sm text-[#1A1A1A] hover:text-[#FF7A1A] hover:bg-white/40 transition-colors"
              >
                Accedi
              </Link>
              <Link
                to="/register"
                data-testid="landing-cta-nav"
                onClick={() => trackInstallEvent("register_opened")}
                className="orio-cta-btn relative px-4 py-2 rounded-full text-sm font-semibold text-black overflow-hidden"
              >
                <span className="relative z-10">Registrati</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/* ---------- HERO ---------- */
/* ---------- LIVE TICKER (rotating community events, ORIO premium style) ---------- */
const LIVE_EVENTS = [
  { tag: "Ritrovato", color: COLORS.emergency, quote: "Sofia a Firenze ha ritrovato Otto in 18 minuti.", sub: "231 utenti allertati nel raggio · 12 min fa", initial: "S", gradient: "linear-gradient(135deg,#FF3B30,#FF7A1A)" },
  { tag: "Installato", color: "#2DBA60",       quote: "Marco a Torino ha installato ORIO adesso.",       sub: "Nuovo membro della rete · 34 sec fa",       initial: "M", gradient: "linear-gradient(135deg,#2DBA60,#34D399)" },
  { tag: "Cassetto",   color: COLORS.match,    quote: "Giulia a Bologna ha caricato il libretto di Kira.", sub: "3 documenti sanitari · 4 min fa",          initial: "G", gradient: "linear-gradient(135deg,#34D399,#2DBA60)" },
  { tag: "Radar",      color: "#8B5CF6",       quote: "Bocconi avvelenati segnalati in via Marconi, Milano.", sub: "12 utenti nella zona avvisati · 1 min fa", initial: "!", gradient: "linear-gradient(135deg,#8B5CF6,#7B61FF)" },
  { tag: "Match",      color: COLORS.match,    quote: "Andrea e Rocky hanno trovato Sara e Otto a Roma.", sub: "Compatibilità 94% · passeggiata organizzata", initial: "A", gradient: "linear-gradient(135deg,#34D399,#2DBA60)" },
  { tag: "SOS chiuso", color: COLORS.emergency, quote: "Alessia a Napoli ha chiuso il SOS di Luna.",     sub: "Cane al sicuro · 340 utenti hanno ricevuto",  initial: "A", gradient: "linear-gradient(135deg,#FF3B30,#E94545)" },
  { tag: "Missione",   color: COLORS.missions, quote: "Rifugio di Palermo cerca stallo per Rocky.",      sub: "3 volontari già disponibili · 6 min fa",     initial: "R", gradient: "linear-gradient(135deg,#FFB020,#FF7A1A)" },
  { tag: "Passeggiata",color: "#5B9EE0",       quote: "Marta e Nala hanno completato 3.2 km al parco.",  sub: "Streak 12 giorni · +8 karma · 2 min fa",     initial: "M", gradient: "linear-gradient(135deg,#5B9EE0,#7B61FF)" },
];

function ReviewsRotator() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % LIVE_EVENTS.length), 4200);
    return () => clearInterval(t);
  }, []);
  const ev = LIVE_EVENTS[i];
  return (
    <section className="relative z-10 py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-6 text-center">
        {/* Live-in-Italia pill above the quote — the only "framing" element */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.22em] mb-6"
             style={{ borderColor: "rgba(45,186,96,0.35)", background: "rgba(45,186,96,0.08)", color: "#1E7E3F" }}>
          <span className="h-1.5 w-1.5 rounded-full bg-[#2DBA60] animate-pulse" />
          Rete ORIO · live in Italia
        </div>

        <motion.div
          key={i}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -14 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          {/* Tiny inline tag — floats above the quote without a card */}
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] font-semibold mb-5"
            style={{ color: ev.color }}
          >
            <span className="h-1 w-1 rounded-full animate-pulse" style={{ background: ev.color, boxShadow: `0 0 8px ${ev.color}` }} />
            {ev.tag}
          </div>
          {/* Pure quote — large display, no box */}
          <p className="font-display text-2xl sm:text-3xl md:text-[34px] leading-[1.2] text-[#1A1A1A] tracking-tight"
             style={{ textShadow: "0 1px 12px rgba(251,247,241,0.7)" }}>
            <span className="opacity-30 mr-0.5" style={{ color: ev.color }}>&ldquo;</span>
            {ev.quote}
            <span className="opacity-30 ml-0.5" style={{ color: ev.color }}>&rdquo;</span>
          </p>
          {/* Sub — discreet italic */}
          <div className="mt-5 text-[12px] sm:text-[13px] text-[#7A7A7A] italic">
            {ev.sub}
          </div>
        </motion.div>

        {/* Tiny indicator dots — barely visible */}
        <div className="mt-8 flex items-center justify-center gap-1.5">
          {LIVE_EVENTS.map((e, k) => (
            <button
              key={k}
              onClick={() => setI(k)}
              data-testid={`live-dot-${k}`}
              aria-label={`Evento ${k + 1}`}
              className="h-1 rounded-full transition-all"
              style={{
                width: i === k ? 16 : 4,
                background: i === k ? e.color : "rgba(180,166,135,0.30)",
              }}
            />
          ))}
        </div>

        {/* Live counter — floating text, no card */}
        <div className="mt-8 text-[11.5px] text-[#9A9A9A]">
          <span className="font-semibold tabular-nums text-[#1A1A1A]">27.480+</span> famiglie italiane · una nuova installazione ogni 4 minuti
        </div>
      </div>
    </section>
  );
}

/* ---------- STORY SCROLL (cinematic mockups + color floods) ---------- */
function StoryScroll() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [scene, setScene] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    if (v < 0.33) setScene(0);
    else if (v < 0.66) setScene(1);
    else setScene(2);
  });

  // Brand-color floods that take over the page — bright on warm cream bg
  const flood = useTransform(
    scrollYProgress,
    [0, 0.2, 0.33, 0.45, 0.66, 0.8, 1],
    [
      "radial-gradient(85% 85% at 50% 50%, rgba(255,59,48,0.30) 0%, rgba(255,176,32,0.10) 35%, rgba(251,247,241,0) 70%)",
      "radial-gradient(85% 85% at 50% 50%, rgba(255,59,48,0.32) 0%, rgba(255,176,32,0.12) 35%, rgba(251,247,241,0) 70%)",
      "radial-gradient(85% 85% at 50% 50%, rgba(255,59,48,0.25) 0%, rgba(255,176,32,0.22) 30%, rgba(251,247,241,0) 75%)",
      "radial-gradient(85% 85% at 50% 50%, rgba(255,176,32,0.32) 0%, rgba(244,184,60,0.18) 35%, rgba(251,247,241,0) 70%)",
      "radial-gradient(85% 85% at 50% 50%, rgba(255,176,32,0.32) 0%, rgba(244,184,60,0.18) 35%, rgba(251,247,241,0) 70%)",
      "radial-gradient(85% 85% at 50% 50%, rgba(255,176,32,0.22) 0%, rgba(52,211,153,0.22) 35%, rgba(251,247,241,0) 75%)",
      "radial-gradient(85% 85% at 50% 50%, rgba(52,211,153,0.30) 0%, rgba(45,186,96,0.15) 35%, rgba(251,247,241,0) 70%)",
    ]
  );

  return (
    <section id="story" ref={ref} className="relative" style={{ height: "240vh" }}>
      <div className="sticky top-0 h-screen overflow-hidden flex items-center justify-center">
        {/* Brand-color flood (bright on warm cream) */}
        <motion.div className="absolute inset-0 z-0 pointer-events-none" style={{ background: flood }} />
        {/* Subtle warm vignette so mockup pops without darkening the page */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 50%, rgba(251,247,241,0) 60%, rgba(251,247,241,0.55) 100%)",
          }}
        />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-5 sm:px-8 flex flex-col items-center text-center">
          {/* Eyebrow + headline ABOVE the mockup (centered, swap by scene) */}
          <SceneHeadline scene={scene} />

          {/* Mockups */}
          <div className="mt-8 sm:mt-10 w-full flex items-center justify-center pb-20 sm:pb-12">
            <DeviceStage scene={scene} />
          </div>

          {/* Bullets/CTA BELOW the mockup */}
          <div className="mt-4 sm:mt-2 w-full">
            <SceneFooter scene={scene} />
          </div>
        </div>

        {/* Scene dots */}
        <div className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-3">
          {[COLORS.emergency, COLORS.missions, COLORS.match].map((c, i) => (
            <div
              key={i}
              className="h-2 rounded-full transition-all duration-500"
              style={{
                width: scene === i ? "26px" : "8px",
                background: scene === i ? c : "rgba(180,166,135,0.30)",
                boxShadow: scene === i ? `0 0 14px ${c}` : "none",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function SceneHeadline({ scene }) {
  const data = [
    {
      color: COLORS.emergency,
      icon: AlertTriangle,
      eyebrow: "SOS emergenza",
      title: "Se perdi il cane, con ORIO allerti tutti in pochi secondi.",
    },
    {
      color: COLORS.missions,
      icon: HeartHandshake,
      eyebrow: "Missioni · sicurezza",
      title: "Zona sotto controllo. Ogni pericolo lo vedete tutti insieme.",
    },
    {
      color: COLORS.match,
      icon: Footprints,
      eyebrow: "Match · compagni",
      title: "Trova compagnia per te e per Otto. Vera. Dal vivo.",
    },
  ][scene];
  const Icon = data.icon;
  return (
    <motion.div
      key={`h-${scene}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-2xl"
    >
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.22em]"
        style={{ borderColor: `${data.color}40`, color: data.color, background: `${data.color}10` }}
      >
        <Icon className="h-3.5 w-3.5" /> {data.eyebrow}
      </div>
      <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.12] pb-1">
        {data.title}
      </h2>
    </motion.div>
  );
}

function SceneFooter({ scene }) {
  const navigate = useNavigate();
  const explanations = [
    {
      title: "Una rete che si attiva in pochi secondi.",
      body: "Premi il tasto rosso: ORIO pubblica la scheda del tuo cane sulla mappa nazionale con QR pubblico e allarga l'allarme da 10 fino a 80 km mano a mano che passano i minuti. Chi lo trova scansiona il QR dal collare o apre il passaporto dalla mappa e ti chiama subito — anche senza avere l'app. Quando il cane è al sicuro, chiudi l'emergenza con un click.",
    },
    {
      title: "Ogni segnalazione fa la differenza. Sicurezza condivisa.",
      body: "Bocconi, zecche, cani vaganti, rifugi che cercano stalli: ogni segnalazione diventa una missione geolocalizzata visibile a chi è nella zona. Chi è vicino agisce, chi è lontano amplifica. Nessun social, nessun algoritmo: solo una rete operativa che protegge tutti i cani insieme.",
    },
    {
      title: "La killer feature di ORIO: compagnia vera, dal vivo.",
      body: "Il tuo cane merita più che chat infinite. E anche tu. Con ORIO Match imposti il raggio, attivi la modalità passeggiata e vedi chi è in giro adesso — persona + cane. Sfogli profili reali, esci con chi ti somiglia, e se scatta il match hai 10 messaggi per accordarvi. Poi tutto dal vivo, nel parco.",
    },
  ];
  if (scene === 0) {
    return (
      <motion.div key="f-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className="mt-8 sm:mt-10 flex flex-col items-center gap-5 max-w-3xl mx-auto text-center"
      >
        <div>
          <div className="font-display text-lg sm:text-xl text-[#1A1A1A] mb-2.5">
            {explanations[0].title}
          </div>
          <p className="text-sm sm:text-[15px] text-[#5C5C5C] leading-relaxed">
            {explanations[0].body}
          </p>
        </div>
        <button
          onMouseEnter={() => { try { navigator.vibrate?.(30); } catch {} }}
          onClick={() => { trackInstallEvent("register_opened"); navigate("/register"); }}
          className="relative px-6 py-3 rounded-full bg-[#FF3B30] text-white font-semibold text-sm hover:bg-[#FF3B30]/90 transition-all orio-pulse flex items-center gap-2"
        >
          <AlertTriangle className="h-4 w-4" /> Test emergenza
        </button>
        <div className="text-xs text-[#7A7A7A]">Pubblica una scheda demo · allarme 10→80 km dinamico</div>
      </motion.div>
    );
  }
  if (scene === 1) {
    return (
      <motion.div key="f-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className="mt-8 sm:mt-10 flex flex-col items-center gap-5 max-w-3xl mx-auto text-center"
      >
        <div>
          <div className="font-display text-lg sm:text-xl text-[#1A1A1A] mb-2.5">
            {explanations[1].title}
          </div>
          <p className="text-sm sm:text-[15px] text-[#5C5C5C] leading-relaxed">
            {explanations[1].body}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2.5 text-xs">
          <Chip color={COLORS.missions} icon={AlertTriangle} label="Pericoli segnalati oggi · 47" />
          <Chip color={COLORS.missions} icon={Award} label="Volontari attivi · 142" />
          <Chip color={COLORS.missions} icon={MapPin} label="87% Italia coperta" />
        </div>
      </motion.div>
    );
  }
  return (
    <motion.div key="f-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
      className="mt-8 sm:mt-10 flex flex-col items-center gap-5 max-w-3xl mx-auto text-center"
    >
      <div>
        <div className="font-display text-lg sm:text-xl text-[#1A1A1A] mb-2.5">
          {explanations[2].title}
        </div>
        <p className="text-sm sm:text-[15px] text-[#5C5C5C] leading-relaxed">
          {explanations[2].body}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2.5 text-xs">
        <Chip color={COLORS.match} icon={Heart} label="4.812 match nel 2026" />
        <Chip color={COLORS.match} icon={Footprints} label="Passeggiate reali · 12/giorno" />
        <Chip color={COLORS.match} icon={Star} label="Karma medio · 4,7 / 5" />
      </div>
    </motion.div>
  );
}
function Chip({ color, icon: Icon, label }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border"
      style={{ borderColor: `${color}40`, background: `${color}12`, color }}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </span>
  );
}

/* ---------- DEVICE STAGE ---------- */
function DeviceStage({ scene }) {
  return (
    <div className="relative w-full flex items-center justify-center">
      <div className="relative">
        <DesktopMock scene={scene} />
        <div className="absolute -bottom-8 sm:-bottom-10 -left-6 sm:-left-16 z-20">
          <PhoneMock scene={scene} />
        </div>
      </div>
    </div>
  );
}

function DesktopMock({ scene }) {
  return (
    <div className="relative w-[300px] sm:w-[460px] md:w-[560px] aspect-[16/10]">
      <div className="absolute inset-0 rounded-[14px] sm:rounded-[18px] bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] p-1.5 sm:p-2 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.85)] border border-[#F0EBE3]">
        <div className="relative w-full h-full rounded-[10px] sm:rounded-[12px] overflow-hidden bg-[#FBF7F1]">
          <DesktopScreen scene={scene} />
        </div>
      </div>
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-20 h-2 bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] rounded-b-xl" />
    </div>
  );
}

function PhoneMock({ scene }) {
  return (
    <div className="relative w-[120px] sm:w-[180px] h-[230px] sm:h-[370px] rounded-[24px] sm:rounded-[36px] bg-gradient-to-b from-[#1f1f1f] to-[#0a0a0a] p-1.5 shadow-[0_30px_50px_-15px_rgba(0,0,0,0.9)] border border-[#F0EBE3]">
        <div className="relative w-full h-full rounded-[20px] sm:rounded-[30px] overflow-hidden bg-[#FBF7F1]">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 sm:w-16 h-3 sm:h-4 rounded-full bg-[#1A1A1A] z-10" />
        <PhoneScreen scene={scene} />
      </div>
    </div>
  );
}

function DesktopScreen({ scene }) {
  return (
    <motion.div key={`d-${scene}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45 }} className="absolute inset-0">
      {scene === 0 && <DesktopEmergency />}
      {scene === 1 && <DesktopMissions />}
      {scene === 2 && <DesktopMatch />}
    </motion.div>
  );
}
function PhoneScreen({ scene }) {
  return (
    <motion.div key={`p-${scene}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="absolute inset-0">
      {scene === 0 && <PhoneEmergency />}
      {scene === 1 && <PhoneMissions />}
      {scene === 2 && <PhoneMatch />}
    </motion.div>
  );
}

/* ===== EMERGENCY — Cockpit ===== */
function DesktopEmergency() {
  return (
    <div className="absolute inset-0 bg-[#FBF7F1] p-2.5 text-[9px] text-[#1A1A1A]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-3.5 rounded-full bg-gradient-to-br from-[#FF3B30] via-[#FFB020] to-[#34D399]" />
          <span className="font-medium text-[10px]">ORIO · Cockpit Emergenza</span>
        </div>
        <span className="px-1.5 py-0.5 rounded-full bg-[#FF3B30]/20 text-[#FF3B30] text-[7px] uppercase tracking-wider animate-pulse">● Live · 2 min</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 h-[calc(100%-1.4rem)]">
        {/* Mappa grande con cerchio SOS + soccorritori */}
        <div className="col-span-2 relative rounded-md bg-[#FAF7F0] overflow-hidden border border-[#F0EBE3]">
          <MapLines />
          {/* Cerchio SOS radius 80km — outer dashed */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-32 w-32 rounded-full border-2 border-dashed border-[#FF3B30]/50"
            style={{ background: "rgba(255,59,48,0.06)" }} />
          {/* Inner intensity ring */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-20 w-20 rounded-full"
            style={{ background: "rgba(255,59,48,0.10)" }} />
          {/* Radius label */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 mt-14 px-1.5 py-0.5 rounded-full bg-white/95 border border-[#F0EBE3] text-[7px] font-bold text-[#FF3B30]">
            80 km
          </div>
          {/* Cane smarrito center */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <span className="absolute inset-0 w-4 h-4 rounded-full bg-[#FF3B30] orio-marker-ping" />
            <span className="relative block w-4 h-4 rounded-full bg-[#FF3B30] border-2 border-white shadow-[0_0_18px_#FF3B30]" />
          </div>
          {/* Soccorritori online (green) */}
          {[
            { top: "22%", left: "20%" },
            { top: "68%", left: "78%" },
            { top: "74%", left: "28%" },
            { top: "28%", left: "72%" },
            { top: "48%", left: "16%" },
          ].map((p, i) => (
            <div key={i} className="absolute h-2.5 w-2.5 rounded-full bg-[#2DBA60] border-2 border-white"
              style={{ ...p, boxShadow: "0 0 8px rgba(45,186,96,0.85)" }} />
          ))}
          {/* Bottom info card */}
          <div className="absolute bottom-1 left-1 right-1 bg-white/95 backdrop-blur rounded-md p-1.5 flex items-center gap-2 border border-[#F0EBE3]">
            <div className="h-7 w-7 rounded-md bg-gradient-to-br from-[#FF3B30]/25 to-[#FFB020]/20 flex items-center justify-center">
              <AlertTriangle className="h-3.5 w-3.5 text-[#FF3B30]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[7.5px] text-[#FF3B30] uppercase tracking-wider font-semibold">Luna · 4 anni · Labrador</div>
              <div className="text-[9px] font-semibold truncate leading-tight">Via Lombardia 24, Milano</div>
            </div>
            <span className="px-1.5 py-0.5 rounded-full bg-[#2DBA60]/12 text-[#2DBA60] text-[7px] font-bold whitespace-nowrap">5 online</span>
          </div>
        </div>
        {/* Stats panel */}
        <div className="flex flex-col gap-1.5">
          <div className="flex-1 rounded-md bg-white border border-[#FF3B30]/30 p-1.5 flex flex-col justify-between">
            <div className="text-[7px] uppercase tracking-wider text-[#7A7A7A]">Allerta diffusa</div>
            <div className="font-display text-xl text-[#FF3B30] leading-none">312</div>
            <div className="text-[7px] text-[#6B6B6B]">utenti raggiunti · 80 km</div>
          </div>
          <div className="flex-1 rounded-md bg-white border border-[#F0EBE3] p-1.5 flex flex-col justify-between">
            <div className="text-[7px] uppercase tracking-wider text-[#7A7A7A]">Passaporto QR</div>
            <div className="font-display text-xl leading-none text-[#1A1A1A]">28</div>
            <div className="text-[7px] text-[#6B6B6B]">scansioni da passanti</div>
          </div>
          <div className="flex-1 rounded-md bg-white border border-[#2DBA60]/30 p-1.5 flex flex-col justify-between">
            <div className="text-[7px] uppercase tracking-wider text-[#7A7A7A]">Soccorritori</div>
            <div className="font-display text-xl leading-none text-[#2DBA60]">5</div>
            <div className="text-[7px] text-[#6B6B6B]">online nel raggio</div>
          </div>
          <button className="rounded-md py-1.5 text-white text-[8.5px] font-semibold inline-flex items-center justify-center gap-1"
            style={{ background: "linear-gradient(135deg,#FF7A1A,#FF5C2A)", boxShadow: "0 4px 10px -3px rgba(255,92,42,0.55)" }}>
            <X className="h-2.5 w-2.5" /> Annulla SOS
          </button>
        </div>
      </div>
    </div>
  );
}
function PhoneEmergency() {
  return (
    <div className="absolute inset-0 bg-[#FBF7F1] text-[#1A1A1A] p-2 pt-7 text-[8px] overflow-hidden">
      {/* Top pill */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
          <span className="text-[7px] uppercase tracking-widest text-[#FF3B30] font-semibold">SOS ATTIVO</span>
        </div>
        <span className="text-[7px] text-[#7A7A7A]">2 min fa</span>
      </div>

      {/* Mappa SOS con cerchio + soccorritori */}
      <div className="relative h-[68px] rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] overflow-hidden">
        {/* Grid map lines */}
        <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 100 68" preserveAspectRatio="none">
          <path d="M0,20 Q30,10 60,25 T100,20" stroke="#C4B79A" strokeWidth="0.3" fill="none" />
          <path d="M0,50 Q40,60 80,45 T100,55" stroke="#C4B79A" strokeWidth="0.3" fill="none" />
        </svg>
        {/* Cerchio SOS radius */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 rounded-full border-2 border-dashed border-[#FF3B30]/60"
          style={{ background: "rgba(255,59,48,0.08)" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-8 rounded-full"
          style={{ background: "rgba(255,59,48,0.14)" }} />
        {/* Cane smarrito center */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <span className="absolute inset-0 h-2.5 w-2.5 rounded-full bg-[#FF3B30] orio-marker-ping" />
          <span className="relative block h-2.5 w-2.5 rounded-full bg-[#FF3B30] border border-white" />
        </div>
        {/* Soccorritori sparsi */}
        {[
          { top: "18%", left: "22%" },
          { top: "65%", left: "78%" },
          { top: "72%", left: "30%" },
          { top: "25%", left: "72%" },
        ].map((p, i) => (
          <div key={i} className="absolute h-2 w-2 rounded-full bg-[#2DBA60] border border-white"
            style={{ ...p, boxShadow: "0 0 6px rgba(45,186,96,0.7)" }} />
        ))}
        {/* Radius chip bottom-left */}
        <div className="absolute bottom-1 left-1 px-1 py-px rounded-full bg-white/95 text-[6px] font-semibold text-[#FF3B30] border border-[#F0EBE3]">
          80 km
        </div>
      </div>

      {/* Live counters */}
      <div className="grid grid-cols-2 gap-1 mt-1.5">
        <div className="rounded-lg bg-white border border-[#F0EBE3] px-1.5 py-1">
          <div className="text-[6px] uppercase tracking-widest text-[#9A9A9A]">Allerta</div>
          <div className="font-display text-[13px] text-[#FF3B30] leading-none mt-0.5">312</div>
          <div className="text-[6px] text-[#7A7A7A]">utenti raggiunti</div>
        </div>
        <div className="rounded-lg bg-white border border-[#F0EBE3] px-1.5 py-1">
          <div className="text-[6px] uppercase tracking-widest text-[#9A9A9A]">Soccorritori</div>
          <div className="font-display text-[13px] text-[#2DBA60] leading-none mt-0.5">4</div>
          <div className="text-[6px] text-[#7A7A7A]">online nel raggio</div>
        </div>
      </div>

      {/* Dog card */}
      <div className="mt-1.5 rounded-xl bg-white border border-[#F0EBE3] p-1.5 flex items-center gap-1.5">
        <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-[#FFB020]/30 to-[#FF3B30]/25 flex items-center justify-center text-[10px]">🐕</div>
        <div className="flex-1 min-w-0">
          <div className="text-[8.5px] font-semibold leading-tight">Luna · Labrador</div>
          <div className="text-[6.5px] text-[#7A7A7A] leading-tight">Via Lombardia 24, Milano</div>
        </div>
        <span className="px-1 py-px rounded-full bg-[#FF3B30]/15 text-[#FF3B30] text-[6px] font-bold">4a</span>
      </div>

      {/* Cancel SOS CTA */}
      <button className="mt-1.5 w-full py-1.5 rounded-full text-white font-semibold text-[9px] inline-flex items-center justify-center gap-1"
        style={{ background: "linear-gradient(135deg,#FF7A1A,#FF5C2A)", boxShadow: "0 4px 10px -3px rgba(255,92,42,0.55)" }}>
        <X className="h-2.5 w-2.5" /> Annulla SOS
      </button>
    </div>
  );
}

/* ===== MISSIONS — Social Proof ===== */
function DesktopMissions() {
  const items = [
    { c: "SOS",       t: "Cane smarrito · Bassotto marrone", d: "Milano · 1,8 km · 12 min", karma: 5,  tone: "#FF3B30", icon: "S" },
    { c: "Pericolo",  t: "Bocconi avvelenati confermati ×3", d: "Roma · 2,3 km · 25 min",   karma: 3,  tone: "#E94545", icon: "⚠" },
    { c: "Rifugio",   t: "Cuccioli da adottare · 6 disponibili", d: "Torino · 5 km · 1 h",   karma: 8,  tone: "#FF7A1A", icon: "🏠" },
    { c: "Trasporto", t: "Da rifugio a famiglia adottante",   d: "Bologna → Firenze · sab", karma: 18, tone: "#FF7A1A", icon: "🚩" },
  ];
  return (
    <div className="absolute inset-0 bg-[#FBF7F1] p-2.5 text-[9px] text-[#1A1A1A]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-3.5 rounded-full bg-gradient-to-br from-[#FF3B30] via-[#FFB020] to-[#34D399]" />
          <span className="font-medium text-[10px]">ORIO · Missioni della community</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="px-1.5 py-0.5 rounded-full bg-[#2DBA60]/12 text-[#2DBA60] text-[7.5px] font-semibold inline-flex items-center gap-0.5">
            <TrendingUp className="h-2 w-2" /> 23 aiutati oggi
          </span>
          <span className="px-1.5 py-0.5 rounded-full bg-[#F4EFE6] text-[#7A7A7A] text-[7.5px] font-medium">
            25 km
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5 h-[calc(100%-1.4rem)]">
        {items.map((m, i) => (
          <div key={i} className="rounded-md bg-white border border-[#F0EBE3] p-1.5 flex items-start gap-1.5 relative overflow-hidden">
            {/* Tone accent line */}
            <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: m.tone }} />
            <div className="h-7 w-7 rounded-md flex items-center justify-center shrink-0 text-white text-[10px] font-bold"
              style={{ background: `linear-gradient(135deg,${m.tone},${m.tone}CC)`, boxShadow: `0 3px 8px -2px ${m.tone}55` }}>
              {m.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className="text-[7px] uppercase tracking-wider font-semibold" style={{ color: m.tone }}>{m.c}</span>
                <span className="px-1 py-px rounded-full text-[6.5px] font-bold inline-flex items-center gap-0.5"
                  style={{ background: "rgba(255,176,32,0.18)", color: "#B8850F" }}>
                  <Sparkles className="h-1.5 w-1.5" /> +{m.karma}
                </span>
              </div>
              <div className="font-medium text-[9px] leading-tight truncate">{m.t}</div>
              <div className="text-[7.5px] text-[#7A7A7A] mt-0.5">{m.d}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function PhoneMissions() {
  const items = [
    {
      k: "sos", label: "SOS", tone: "#FF3B30", bg: "rgba(255,59,48,0.10)",
      title: "Cane smarrito · Milano",
      sub: "Bassotto marrone, 1,8 km da te",
      km: "1,8 km", karma: "+5", when: "12 min",
    },
    {
      k: "hazard", label: "Pericolo", tone: "#E94545", bg: "rgba(233,69,69,0.10)",
      title: "Bocconi avvelenati",
      sub: "Parco Sempione, confermato ×3",
      km: "2,3 km", karma: "+3", when: "45 min",
    },
    {
      k: "mission", label: "Missione", tone: "#FF7A1A", bg: "rgba(255,122,26,0.10)",
      title: "Trasporto rifugio → adottante",
      sub: "Bologna → Firenze, sabato",
      km: "5 km", karma: "+18", when: "1 h",
    },
  ];
  const glyph = { sos: "S", hazard: "⚠", mission: "🚩" };
  return (
    <div className="absolute inset-0 bg-[#FBF7F1] text-[#1A1A1A] p-2 pt-7 text-[8px] overflow-hidden">
      {/* Top segmented pill: Others / Create */}
      <div className="flex items-center gap-1 mb-1.5">
        <div className="flex-1 rounded-full bg-white border border-[#F0EBE3] p-0.5 flex text-[6px] font-semibold">
          <span className="flex-1 text-center py-0.5 rounded-full text-white" style={{ background: "linear-gradient(135deg,#FF7A1A,#FF5C2A)" }}>Da altri</span>
          <span className="flex-1 text-center py-0.5 text-[#9A9A9A]">Crea</span>
        </div>
        <span className="px-1.5 py-0.5 rounded-full bg-[#2DBA60]/12 text-[#2DBA60] text-[6px] font-bold">
          23 aiutati oggi
        </span>
      </div>

      {/* Feed list */}
      <div className="space-y-1">
        {items.map((m) => (
          <div key={m.k} className="rounded-xl bg-white border border-[#F0EBE3] p-1.5 flex items-center gap-1.5">
            <div className="h-7 w-7 rounded-lg flex items-center justify-center text-[10px] font-bold text-white shrink-0"
              style={{ background: `linear-gradient(135deg,${m.tone},${m.tone}CC)`, boxShadow: `0 4px 8px -3px ${m.tone}66` }}>
              {glyph[m.k]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className="text-[6px] uppercase tracking-widest font-semibold" style={{ color: m.tone }}>{m.label}</span>
                <span className="text-[6px] text-[#9A9A9A]">· {m.when}</span>
              </div>
              <div className="text-[8.5px] font-semibold leading-tight truncate">{m.title}</div>
              <div className="text-[6.5px] text-[#7A7A7A] leading-tight truncate">{m.sub}</div>
            </div>
            <div className="flex flex-col items-end gap-0.5 shrink-0">
              <span className="px-1 py-px rounded-full bg-[#F4EFE6] text-[#1A1A1A] text-[6px] font-semibold">{m.km}</span>
              <span className="px-1 py-px rounded-full text-[6px] font-bold" style={{ background: "rgba(255,176,32,0.18)", color: "#B8850F" }}>
                {m.karma} karma
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="absolute inset-x-2 bottom-2 rounded-full py-1.5 text-white text-[8px] font-semibold text-center inline-flex items-center justify-center gap-1"
        style={{ background: "linear-gradient(135deg,#FF7A1A,#FF5C2A)", boxShadow: "0 6px 14px -4px rgba(255,92,42,0.55)" }}>
        <Zap className="h-2.5 w-2.5" /> Segnala pericolo
      </div>
    </div>
  );
}

/* ===== MATCH — Two cards touching ===== */
function DesktopMatch() {
  return (
    <div className="absolute inset-0 bg-[#FBF7F1] p-2.5 text-[9px] text-[#1A1A1A]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-3.5 rounded-full bg-gradient-to-br from-[#FF3B30] via-[#FFB020] to-[#34D399]" />
          <span className="font-medium text-[10px]">ORIO · Match del giorno</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="px-1.5 py-0.5 rounded-full bg-white border border-[#F0EBE3] text-[7.5px] font-semibold inline-flex items-center gap-0.5">
            <Star className="h-2 w-2 text-[#7B61FF] fill-[#7B61FF]" /> 3 Super
          </span>
          <span className="px-1.5 py-0.5 rounded-full bg-white border border-[#F0EBE3] text-[7.5px] font-semibold inline-flex items-center gap-0.5">
            <Sparkles className="h-2 w-2 text-[#FFB020]" /> 190
          </span>
        </div>
      </div>
      <div className="relative h-[calc(100%-1.4rem)] flex items-center justify-center gap-1">
        <MatchCard name="Otto" breed="Beagle · 3 anni" tag="1,2 km · 92% match" left slug="match-walking-v2" />
        <div className="relative z-10 h-11 w-11 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "linear-gradient(135deg,#2DBA60,#34D399)", boxShadow: "0 0 22px rgba(45,186,96,0.55)" }}>
          <Heart className="h-5 w-5 text-white fill-white" />
        </div>
        <MatchCard name="Pepe" breed="Border · 5 anni" tag="parco · gioca con tutti" slug="feat-match" />
      </div>
      {/* Bottom stripe */}
      <div className="absolute inset-x-2.5 bottom-1.5 rounded-md bg-white border border-[#F0EBE3] px-1.5 py-1 flex items-center gap-1.5">
        <div className="h-4 w-4 rounded-full flex items-center justify-center text-white"
          style={{ background: "linear-gradient(135deg,#7B61FF,#5B44E0)" }}>
          <Star className="h-2 w-2 fill-white" />
        </div>
        <span className="text-[7.5px] font-semibold text-[#1A1A1A] flex-1">Super Like — appari 5× più in alto oggi</span>
        <span className="px-1.5 py-0.5 rounded-full text-[7px] font-bold text-white"
          style={{ background: "linear-gradient(135deg,#7B61FF,#5B44E0)" }}>USA</span>
      </div>
    </div>
  );
}
function MatchCard({ name, breed, tag, left, slug }) {
  return (
    <div className={`relative w-32 h-full rounded-xl border bg-white p-1.5 flex flex-col justify-between overflow-hidden ${left ? "" : ""}`}
      style={{ borderColor: "rgba(45,186,96,0.35)", boxShadow: "0 8px 18px -10px rgba(0,0,0,0.15)" }}>
      {/* Real ORIO illustration in photo area */}
      <div className="relative h-16 rounded-lg overflow-hidden">
        <IlloImg slug={slug || "match-walking-v2"} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(0,0,0,0) 40%,rgba(0,0,0,0.4) 100%)" }} />
        <span className="absolute top-0.5 left-0.5 px-1 py-px rounded-full bg-white/95 text-[6px] font-bold text-[#2DBA60]">92%</span>
        <span className="absolute top-0.5 right-0.5 px-1 py-px rounded-full bg-white/95 text-[6px] font-semibold text-[#1A1A1A]">1,2 km</span>
      </div>
      <div className="mt-1">
        <div className="font-display text-[12px] leading-tight">{name}</div>
        <div className="text-[7.5px] text-[#7A7A7A] leading-tight">{breed}</div>
        <div className="mt-1 inline-block px-1 py-px rounded-full bg-[#2DBA60]/12 text-[#2DBA60] text-[6.5px] font-semibold">{tag}</div>
      </div>
    </div>
  );
}
function PhoneMatch() {
  return (
    <div className="absolute inset-0 text-[#1A1A1A] p-2 pt-7 text-[8px] overflow-hidden"
      style={{ background: "radial-gradient(80% 60% at 50% 20%, rgba(52,211,153,0.20) 0%, #FBF7F1 70%)" }}>
      {/* Top stripe: Super Likes + Karma */}
      <div className="flex items-center gap-1 mb-1.5">
        <div className="flex-1 rounded-full bg-white border border-[#F0EBE3] px-1.5 py-0.5 flex items-center gap-1">
          <Star className="h-2 w-2 text-[#7B61FF] fill-[#7B61FF]" />
          <span className="text-[6.5px] font-semibold text-[#1A1A1A]">3</span>
          <span className="text-[6px] text-[#9A9A9A]">Super Like</span>
        </div>
        <div className="rounded-full bg-white border border-[#F0EBE3] px-1.5 py-0.5 flex items-center gap-1">
          <Sparkles className="h-2 w-2 text-[#FFB020]" />
          <span className="text-[6.5px] font-semibold text-[#B8850F]">190</span>
        </div>
      </div>

      {/* Vertical stacked cards (Trade Republic vibe) */}
      <div className="relative" style={{ aspectRatio: "3/4" }}>
        {/* Back card (peek) */}
        <div className="absolute inset-x-3 top-3 bottom-6 rounded-2xl border border-[#F0EBE3] bg-white/70 -z-0" />
        {/* Front card */}
        <div className="absolute inset-0 rounded-2xl overflow-hidden border border-[#E8E1CC] bg-white"
          style={{ boxShadow: "0 12px 28px -12px rgba(0,0,0,0.22)" }}>
          {/* Real ORIO illustration (girl + dog) */}
          <IlloImg slug="match-walking-v2" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.65) 100%)" }} />
          {/* Distance pill top-right */}
          <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-white/95 text-[6.5px] font-semibold text-[#1A1A1A] inline-flex items-center gap-0.5">
            <MapPin className="h-2 w-2" /> 1,2 km
          </div>
          {/* Compatibility badge top-left */}
          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[6.5px] font-bold text-white inline-flex items-center gap-0.5"
            style={{ background: "linear-gradient(135deg,#2DBA60,#34D399)" }}>
            <Heart className="h-2 w-2 fill-white" /> 92%
          </div>
          {/* Bottom info */}
          <div className="absolute inset-x-1.5 bottom-1.5 text-white">
            <div className="text-[6px] uppercase tracking-widest text-white/80">Compagno di passeggiata</div>
            <div className="font-display text-[13px] leading-none mt-0.5">Sara · Otto</div>
            <div className="text-[6.5px] text-white/90 leading-tight mt-0.5">Border Collie · 3 anni · gioca con tutti</div>
            <div className="mt-1 flex gap-0.5 flex-wrap">
              <span className="px-1 py-px rounded-full bg-white/90 text-[5.5px] text-[#1A1A1A] font-semibold">passeggiate</span>
              <span className="px-1 py-px rounded-full bg-white/90 text-[5.5px] text-[#1A1A1A] font-semibold">parco</span>
              <span className="px-1 py-px rounded-full bg-white/90 text-[5.5px] text-[#1A1A1A] font-semibold">sport</span>
            </div>
          </div>
        </div>
      </div>

      {/* Swipe buttons row */}
      <div className="mt-2 flex items-center justify-center gap-2">
        <div className="h-6 w-6 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center"
          style={{ boxShadow: "0 3px 8px -3px rgba(0,0,0,0.15)" }}>
          <X className="h-2.5 w-2.5 text-[#7A7A7A]" />
        </div>
        <div className="h-8 w-8 rounded-full flex items-center justify-center text-white"
          style={{ background: "linear-gradient(135deg,#7B61FF,#5B44E0)", boxShadow: "0 6px 12px -3px rgba(123,97,255,0.55)" }}>
          <Star className="h-3 w-3 fill-white" />
        </div>
        <div className="h-8 w-8 rounded-full flex items-center justify-center text-white"
          style={{ background: "linear-gradient(135deg,#2DBA60,#34D399)", boxShadow: "0 6px 12px -3px rgba(45,186,96,0.55)" }}>
          <Heart className="h-3 w-3 fill-white" />
        </div>
      </div>
    </div>
  );
}

function MapLines() {
  return (
    <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 200 100" preserveAspectRatio="none">
      <path d="M0,30 Q50,10 100,40 T200,30" stroke="rgba(180,166,135,0.55)" strokeWidth="0.5" fill="none" />
      <path d="M0,60 Q60,80 130,55 T200,70" stroke="rgba(180,166,135,0.50)" strokeWidth="0.5" fill="none" />
      <path d="M30,0 L40,100" stroke="rgba(180,166,135,0.30)" strokeWidth="0.4" />
      <path d="M120,0 L130,100" stroke="rgba(180,166,135,0.30)" strokeWidth="0.4" />
    </svg>
  );
}

/* ---------- MAP LIVE BLOCK ---------- */
const MAP_FEATURES = [
  { id: "emergency", color: "#FF3B30", icon: AlertTriangle, title: "Emergenze SOS", short: "Allarme che cresce 10→80 km",
    detail: "Quando un cane viene smarrito vicino a te, ricevi una notifica push istantanea con foto, posizione e dettagli identificativi. La rete si attiva in secondi.",
    pos: { x: 50, y: 35 }, pulse: true },
  { id: "hazard", color: "#FFB020", icon: Shield, title: "Pericoli segnalati", short: "Bocconi, cani sciolti, vetri",
    detail: "Ogni proprietario puo marcare un pericolo sulla mappa. ORIO avvisa chi e entro 1 km, in tempo reale.",
    pos: { x: 28, y: 58 } },
  { id: "dog_park", color: "#8b5cf6", icon: PawPrint, title: "Aree cani vicine", short: "Parchi e aree libere autorizzate",
    detail: "Tutti i parchi cani della tua citta, gia geolocalizzati. Sai sempre dove portarlo per la prossima passeggiata.",
    pos: { x: 68, y: 52 } },
  { id: "vet", color: "#06b6d4", icon: HeartHandshake, title: "Veterinari vicini", short: "Cliniche aperte ora",
    detail: "In emergenza non perdi tempo a cercare su Google: ORIO ti mostra i veterinari piu vicini con orari e contatti.",
    pos: { x: 78, y: 30 } },
  { id: "mission", color: "#FFB020", icon: Briefcase, title: "Missioni di soccorso", short: "Salvataggi e staffette",
    detail: "Trasporti, stalli, adozioni urgenti, cure veterinarie: tutto coordinato sulla mappa, con dossier dettagliato.",
    pos: { x: 38, y: 72 } },
  { id: "notify", color: "#34D399", icon: Bell, title: "Notifiche intelligenti", short: "Solo cio che ti riguarda",
    detail: "Emergenze con raggio dinamico (10→80 km nel tempo), pericoli entro 1 km, missioni nella tua citta. Niente notifiche inutili.",
    pos: { x: 18, y: 28 } },
];

// Live-feed typing lines — cycled through with a fake typewriter effect
// to make ORIO Radar feel alive and always vigilant.
const RADAR_TYPING_LINES = [
  { icon: "🌡️", tone: "#E94545", text: "Caldo estremo a Roma · 39°C, evita l'asfalto tra le 11 e le 18" },
  { icon: "🍖", tone: "#B91C1C", text: "Bocconi avvelenati segnalati in via Marconi 30, Milano · evita l'area" },
  { icon: "🔥", tone: "#F4813C", text: "Incendio rilevato a 12 km da Cagliari · monitoraggio satellitare attivo" },
  { icon: "🕷️", tone: "#7C3AED", text: "Zecche in aumento nei parchi di Bologna · applica antiparassitario" },
  { icon: "🐕", tone: "#FF7A1A", text: "Cane smarrito a Firenze · foto e posizione condivise con 340 utenti" },
  { icon: "⛈️", tone: "#3B82F6", text: "Allerta meteo arancione in Campania · temporali forti previsti" },
  { icon: "🏥", tone: "#2DBA60", text: "3 veterinari aperti ora entro 2 km da te · tap per chiamare" },
  { icon: "🐾", tone: "#F4B83C", text: "Area cani segnalata come sporca a Parco Sempione · pulizia richiesta" },
  { icon: "💊", tone: "#8B5CF6", text: "Vaccino antirabbica in scadenza per Luna · promemoria attivo" },
  { icon: "🌊", tone: "#0EA5E9", text: "Alluvione in Emilia · zone rosse aggiornate ora dalla Protezione Civile" },
  { icon: "🐺", tone: "#B45309", text: "Avvistamento lupi nell'entroterra ligure · tieni il cane al guinzaglio" },
  { icon: "🚗", tone: "#DC2626", text: "Cane investito segnalato tangenziale Roma est · staffetta veterinaria in corso" },
  { icon: "❤️", tone: "#EC4899", text: "Nuovo match compatibile a 400m · Bella cerca un compagno di corsa" },
  { icon: "🏠", tone: "#059669", text: "Rifugio Torino cerca stallo urgente per Rocky · adottabile a Novembre" },
  { icon: "☀️", tone: "#F59E0B", text: "UV alto oggi in Sardegna · protezione naso e cuscinetti consigliata" },
];

function useTypewriter(lines, charMs = 22, holdMs = 2600) {
  const [idx, setIdx] = React.useState(0);
  const [text, setText] = React.useState("");
  const [phase, setPhase] = React.useState("typing"); // typing | holding | erasing
  React.useEffect(() => {
    const target = lines[idx % lines.length].text;
    let t;
    if (phase === "typing") {
      if (text.length < target.length) {
        t = setTimeout(() => setText(target.slice(0, text.length + 1)), charMs);
      } else {
        t = setTimeout(() => setPhase("holding"), holdMs);
      }
    } else if (phase === "holding") {
      t = setTimeout(() => setPhase("erasing"), holdMs);
    } else if (phase === "erasing") {
      if (text.length > 0) {
        t = setTimeout(() => setText(text.slice(0, -1)), 10);
      } else {
        setIdx((i) => i + 1);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [text, phase, idx, lines, charMs, holdMs]);
  const current = lines[idx % lines.length];
  return { text, current };
}

function MapLiveBlock() {
  const { text: typed, current: liveLine } = useTypewriter(RADAR_TYPING_LINES);
  return (
    <section id="mappa-live" className="relative z-10 py-14 sm:py-20 overflow-hidden">
      {/* Ambient warm+violet aura */}
      <div aria-hidden className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(60% 45% at 50% 30%, rgba(139,92,246,0.10) 0%, rgba(6,182,212,0.05) 35%, rgba(0,0,0,0) 70%)" }} />

      <div className="max-w-5xl mx-auto px-5 sm:px-6 relative">
        {/* Animated header (title + paragraph enter on scroll) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.75, ease: "easeOut" }}
          className="text-center max-w-2xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.22em] mb-4"
            style={{ borderColor: "rgba(139,92,246,0.40)", color: "#a78bfa", background: "rgba(139,92,246,0.10)" }}
          >
            <MapPin className="h-3 w-3" /> ORIO Radar
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.12] pb-1"
          >
            Il tuo cane cammina<br />
            <span className="orio-grad-text">al sicuro. Sempre.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.30 }}
            className="mt-3 text-[#5C5C5C] text-sm sm:text-base max-w-lg mx-auto leading-relaxed"
          >
            Un radar in tempo reale che ti avvisa su <strong>incendi, caldo pericoloso, zecche, bocconi avvelenati</strong> e allerte meteo intorno a te. Dati ufficiali NASA, Copernicus e Protezione Civile.
          </motion.p>
        </motion.div>

        {/* Radar live canvas — full-bleed feel · no visible edges. Reduced top gap for tighter feel. */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.9, delay: 0.35, ease: "easeOut" }}
          className="mt-4 sm:mt-6 relative max-w-4xl mx-auto w-full h-[220px] sm:h-[260px] md:h-[290px]"
          data-testid="radar-live-canvas"
        >
          {/* Blurred pastel map — edge fades via radial mask, no visible box */}
          <div className="absolute inset-0"
            style={{
              WebkitMaskImage:
                "radial-gradient(ellipse 65% 60% at 50% 50%, #000 0%, #000 40%, rgba(0,0,0,0.7) 65%, rgba(0,0,0,0.3) 85%, transparent 100%)",
              maskImage:
                "radial-gradient(ellipse 65% 60% at 50% 50%, #000 0%, #000 40%, rgba(0,0,0,0.7) 65%, rgba(0,0,0,0.3) 85%, transparent 100%)",
            }}>
            <BlurredORIOMap accent={liveLine?.tone || "#FF7A1A"} />
          </div>

          {/* Apple-style dominant transparent banner with ORIO radar pulse */}
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
            className="absolute inset-x-3 sm:inset-x-8 top-1/2 -translate-y-1/2"
          >
            {/* Outer wrapper hosts the concentric pulse rings so they can overflow beyond the banner */}
            <div className="relative mx-auto max-w-[720px]">
              {/* Concentric ORIO radar pulse rings (soft, brand-colored) */}
              <div aria-hidden className="orio-radar-ring" style={{ borderRadius: 28 }} />
              <div aria-hidden className="orio-radar-ring orio-radar-ring--b" style={{ borderRadius: 28 }} />
              <div aria-hidden className="orio-radar-ring orio-radar-ring--c" style={{ borderRadius: 28 }} />

              {/* ORIO gradient border wrapper — 1.5px animated gradient rim */}
              <div
                className="relative rounded-[28px] p-[1.5px]"
                style={{
                  background:
                    "linear-gradient(120deg, rgba(255,59,48,0.55) 0%, rgba(255,122,26,0.55) 25%, rgba(255,176,32,0.55) 50%, rgba(52,211,153,0.55) 75%, rgba(91,158,224,0.55) 100%)",
                  backgroundSize: "300% 300%",
                  animation: "orio-bg-shift 10s ease infinite",
                  boxShadow:
                    "0 30px 60px -20px rgba(255,122,26,0.28), 0 10px 30px -10px rgba(52,211,153,0.20)",
                }}
              >
                <div
                  data-testid="radar-typing-bar"
                  className="relative rounded-[26px] px-4 sm:px-7 py-4 sm:py-6 flex items-start gap-3 sm:gap-5 overflow-hidden"
                  style={{
                    background: "rgba(255,255,255,0.62)",
                    backdropFilter: "blur(28px) saturate(1.4)",
                    WebkitBackdropFilter: "blur(28px) saturate(1.4)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.85), inset 0 -1px 0 rgba(0,0,0,0.03)",
                  }}
                >
                  {/* Sweeping ORIO scanline behind content */}
                  <div aria-hidden className="orio-radar-sweep" />
                  {/* ORIO logo mark */}
                  <div className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-2xl flex items-center justify-center shrink-0"
                       style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
                                boxShadow: "0 10px 24px -8px rgba(255,122,26,0.55)" }}>
                    <PawPrint className="h-4.5 w-4.5 sm:h-5.5 sm:w-5.5 text-white" strokeWidth={2.5} />
                  </div>
                  <div className="relative flex-1 min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold text-[#7A6E56] inline-flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full animate-pulse"
                            style={{ background: liveLine?.tone || "#FF7A1A" }} />
                      ORIO Radar · live
                    </div>
                    <div className="mt-1.5 text-[15px] sm:text-[20px] leading-[1.35] text-[#0F0F0F] font-semibold min-h-[72px] sm:min-h-[58px] tracking-[-0.01em]">
                      <span className="mr-1.5 text-[17px] sm:text-[22px]">{liveLine?.icon}</span>
                      {typed}
                      <span
                        className="inline-block ml-0.5 w-[3px] h-[0.9em] align-middle rounded-sm animate-pulse"
                        style={{ background: liveLine?.tone || "#FF7A1A", opacity: 0.85 }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Icon-only feature grid — clean, no example card underneath */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-20px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-8 sm:mt-12 grid grid-cols-3 md:grid-cols-6 gap-2.5 sm:gap-3 max-w-3xl mx-auto"
        >
          {MAP_FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.id}
                data-testid={`map-feature-${f.id}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: 0.10 + i * 0.06 }}
                className="rounded-2xl border p-3 flex flex-col items-center text-center gap-1.5"
                style={{
                  background: "#FFFFFF",
                  borderColor: "#F0EBE3",
                  boxShadow: "0 4px 14px -8px rgba(0,0,0,0.08)",
                }}
              >
                <div className="h-10 w-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${f.color}18`, color: f.color }}>
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <div className="text-[11px] font-semibold leading-tight text-[#1A1A1A]">{f.title}</div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* Blurred painterly ORIO faux-map — pure CSS/SVG, no external tiles.
   Used as the dreamy always-in-background layer under the typing bar. */
function BlurredORIOMap({ accent = "#FF7A1A" }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <svg viewBox="0 0 800 450" className="w-full h-full" preserveAspectRatio="xMidYMid slice"
           style={{ filter: "blur(4px) saturate(1.05)" }}>
        {/* Warm cream base */}
        <rect width="800" height="450" fill="#F4EFE6" />
        {/* Sage rivers */}
        <path d="M -20 220 Q 200 180 400 240 T 820 200" stroke="#B8CFA5" strokeWidth="26" fill="none" opacity="0.45" />
        <path d="M -20 100 Q 260 70 500 130 T 820 90" stroke="#C7D8B9" strokeWidth="18" fill="none" opacity="0.45" />
        {/* Streets */}
        <path d="M -20 320 Q 200 300 400 340 T 820 320" stroke="#F0E2CE" strokeWidth="10" fill="none" opacity="0.7" />
        <path d="M 100 -20 L 130 470" stroke="#F0E2CE" strokeWidth="8" fill="none" opacity="0.6" />
        <path d="M 320 -20 L 350 470" stroke="#F0E2CE" strokeWidth="8" fill="none" opacity="0.55" />
        <path d="M 560 -20 L 590 470" stroke="#F0E2CE" strokeWidth="8" fill="none" opacity="0.55" />
        {/* Parks */}
        <ellipse cx="220" cy="150" rx="80" ry="52" fill="#C2D6A6" opacity="0.55" />
        <ellipse cx="620" cy="330" rx="110" ry="70" fill="#C2D6A6" opacity="0.55" />
        <ellipse cx="440" cy="90" rx="60" ry="42" fill="#D6DDBF" opacity="0.5" />
        {/* Building blocks */}
        <rect x="380" y="200" width="60" height="40" rx="6" fill="#E8D3B5" opacity="0.55" />
        <rect x="500" y="160" width="70" height="50" rx="6" fill="#E4CBA9" opacity="0.55" />
        <rect x="140" y="360" width="90" height="55" rx="6" fill="#E8D3B5" opacity="0.55" />
        <rect x="640" y="220" width="60" height="40" rx="6" fill="#E4CBA9" opacity="0.55" />
        {/* Accent pulses (small dots) — hint at the "live" nature */}
        <circle cx="230" cy="200" r="4" fill={accent} opacity="0.9">
          <animate attributeName="opacity" values="0.9;0.4;0.9" dur="1.6s" repeatCount="indefinite" />
        </circle>
        <circle cx="230" cy="200" r="14" fill={accent} opacity="0.15">
          <animate attributeName="r" values="10;22;10" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.15;0;0.15" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="580" cy="290" r="4" fill={accent} opacity="0.9">
          <animate attributeName="opacity" values="0.9;0.4;0.9" dur="1.8s" repeatCount="indefinite" />
        </circle>
        <circle cx="580" cy="290" r="14" fill={accent} opacity="0.15">
          <animate attributeName="r" values="10;22;10" dur="2.2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.15;0;0.15" dur="2.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="420" cy="120" r="3.5" fill={accent} opacity="0.8" />
      </svg>
      {/* Extra painterly grain */}
      <div className="absolute inset-0 opacity-40"
           style={{ background: "radial-gradient(circle at 20% 30%, rgba(255,176,32,0.10), transparent 55%), radial-gradient(circle at 80% 70%, rgba(139,92,246,0.08), transparent 55%)" }} />
    </div>
  );
}

/* ---------- IDENTITÀ DIGITALE (Scheda cane + Cassetto, scroll alternato) ---------- */
function IdentityBlock() {
  const steps = [
    {
      eyebrow: "Il tuo profilo · Community",
      color: "#FFB020",
      icon: PawPrint,
      title: "Non un'app. Una community italiana che si aiuta.",
      lead: "Crei il tuo profilo, quello del tuo cane, ed entri in una rete di famiglie che si aiutano davvero: segnalano pericoli, ritrovano cani smarriti, condividono esperienze e difendono i più deboli. Zero social, tutto operativo.",
      bullets: [
        "Profilo cane con foto, microchip, tratti · QR per il collare",
        "Vista pubblica minima per chi lo trova · attivazione automatica in SOS",
        "Segnali pericoli e li vedi da chi ti sta vicino (bocconi, buche, veicoli)",
        "Missioni di comunità: torre gattile, adozioni verificate, staffette",
      ],
      footer: "Perché nessun cane finisca mai solo, e nessun padrone da solo.",
      mockup: "passport",
      align: "left",
    },
    {
      eyebrow: "Cassetto & Veterinario",
      color: "#34D399",
      icon: FileLock2,
      title: "Documenti sempre con te. Veterinario a un tap.",
      lead: "In un'urgenza serve tutto e subito: libretto vaccinazioni, terapie in corso, chi chiamare. ORIO li porta nel telefono cifrati, ricorda le scadenze, e ti mostra il veterinario aperto più vicino con navigazione in un solo tap.",
      bullets: [
        "Vaccini, esami, terapie sempre con te · anche offline",
        "Notifiche prima di ogni scadenza (vaccino, antiparassitario, richiamo)",
        "Veterinari nella tua zona · aperti ora · con chiamata diretta",
        "Cifrato lato server, accessibile solo da te",
      ],
      footer: "Perché quando il tuo cane sta male, non hai tempo di cercare.",
      mockup: "drawer",
      align: "right",
    },
    {
      eyebrow: "Match · Stories · Corsi",
      color: "#2DBA60",
      icon: Heart,
      title: "Match, Stories, uscite, corsi: la vita sociale del tuo cane.",
      lead: "Il Match di ORIO oggi è già molto più di uno swipe: dalle Stories dei cani in giro adesso — video da 10 secondi che spariscono in 12 ore — ai match veri con altri padroni per una passeggiata insieme. Un match nasce anche solo guardando una storia, reagendo con un 👋, salutando qualcuno in zona. E i corsi con educatori certificati arrivano in app entro l'anno.",
      bullets: [
        "Stories: video reali di cani in passeggiata ora — guarda, reagisci, incontra",
        "Passeggiate: raggio km, modalità live, compagnia dal vivo nel parco",
        "Esci con qualcuno: incontri persone compatibili per socialità, non chat",
        "Corsi in arrivo: educatori certificati, videolezioni + sessioni pratiche",
        "Karma reciproco dopo ogni incontro · fiducia costruita nel tempo",
      ],
      footer: "Perché il tuo cane merita compagni veri. E anche tu.",
      mockup: "match",
      align: "left",
    },
  ];
  return (
    <section id="identita" className="relative z-10 ">
      {steps.map((s, i) => (
        <IdentityStep key={s.eyebrow} {...s} reversed={s.align === "right"} index={i} />
      ))}
    </section>
  );
}

function IdentityStep({ eyebrow, color, icon: Icon, title, lead, bullets, footer, mockup, reversed }) {
  return (
    <div className="relative py-20 sm:py-28 overflow-hidden">
      {/* Stronger color halo to soften the cream */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[20%] w-[600px] h-[600px] rounded-full opacity-65"
          style={{
            background: `radial-gradient(circle, ${color}38 0%, ${color}10 50%, transparent 80%)`,
            filter: "blur(50px)",
            ...(reversed ? { right: "-10%" } : { left: "-10%" }),
          }} />
        <div className="absolute bottom-[10%] w-[400px] h-[400px] rounded-full opacity-40"
          style={{
            background: `radial-gradient(circle, ${color}28 0%, transparent 70%)`,
            filter: "blur(60px)",
            ...(reversed ? { left: "5%" } : { right: "5%" }),
          }} />
      </div>
      <div className={`max-w-6xl mx-auto px-5 sm:px-6 relative grid md:grid-cols-2 gap-10 lg:gap-16 items-center ${reversed ? "md:[&>*:first-child]:order-2" : ""}`}>
        <motion.div
          initial={{ opacity: 0, x: reversed ? 20 : -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.22em] mb-5"
            style={{ borderColor: `${color}50`, color, background: `${color}10` }}>
            <Icon className="h-3 w-3" /> {eyebrow}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.12] pb-1">
            {title.split(". ").map((part, i, arr) => (
              <span key={i}>
                {part}{i < arr.length - 1 ? "." : ""}
                {i < arr.length - 1 && <br />}
              </span>
            ))}
          </h2>
          <p className="mt-5 text-[#5C5C5C] text-base leading-relaxed">{lead}</p>
          <ul className="mt-6 space-y-2.5">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-[#1A1A1A]">
                <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-[#7A7A7A] italic border-l-2 pl-4" style={{ borderColor: `${color}40` }}>
            {footer}
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: reversed ? -20 : 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative"
        >
          {mockup === "passport" ? <PassportMockup /> : mockup === "drawer" ? <DrawerMockup /> : <MatchIdentityMockup />}
        </motion.div>
      </div>
    </div>
  );
}

function PassportMockup() {
  return (
    <div className="relative max-w-sm mx-auto">
      <div aria-hidden className="absolute -inset-8 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,176,32,0.25), transparent 70%)" }} />
      <div className="relative rounded-3xl border border-[#F0EBE3] bg-[#FBF7F1] p-5 shadow-2xl">
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A]">Passaporto digitale</div>
          <div className="ml-auto h-1.5 w-1.5 rounded-full bg-[#34D399] animate-pulse" />
          <span className="text-[10px] text-[#34D399] font-semibold">ATTIVO</span>
        </div>
        <div className="mt-4 flex gap-4">
          {/* Photo placeholder — stylised dog silhouette */}
          <div className="relative h-20 w-20 rounded-2xl overflow-hidden border border-[#F0EBE3] shrink-0"
            style={{ background: "linear-gradient(135deg,#FFB020 0%,#FF7A1A 100%)" }}>
            <div className="absolute inset-0 flex items-center justify-center">
              <PawPrint className="h-8 w-8 text-white/85" strokeWidth={2.2} />
            </div>
            {/* Karma badge floating */}
            <div className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center"
              style={{ boxShadow: "0 3px 8px -2px rgba(0,0,0,0.15)" }}>
              <Sparkles className="h-3 w-3 text-[#FFB020]" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl leading-tight">Luna</div>
            <div className="text-xs text-[#7A7A7A]">Labrador · 4 anni · 28 kg</div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                style={{ background: "#34D39920", color: "#34D399" }}>
                <ShieldCheck className="h-2.5 w-2.5" /> Verificato
              </div>
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                style={{ background: "rgba(255,176,32,0.18)", color: "#B8850F" }}>
                <Sparkles className="h-2.5 w-2.5" /> 190 karma
              </div>
            </div>
          </div>
        </div>
        {/* Walking mode toggle */}
        <div className="mt-4 rounded-xl bg-white border border-[#2DBA60]/35 px-3 py-2 flex items-center gap-2"
          style={{ background: "rgba(45,186,96,0.08)" }}>
          <div className="h-7 w-7 rounded-lg bg-white border border-[#F0EBE3] flex items-center justify-center">
            <Footprints className="h-3.5 w-3.5 text-[#2DBA60]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11.5px] font-semibold text-[#1A1A1A]">Modalità passeggiata</div>
            <div className="text-[10px] text-[#5C5C5C]">Attiva per 2h · raggio 30 km</div>
          </div>
          <div className="h-4 w-7 rounded-full bg-[#2DBA60] relative">
            <div className="absolute top-0.5 right-0.5 h-3 w-3 rounded-full bg-white shadow" />
          </div>
        </div>
        {/* Chip + peso */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
          <div className="rounded-lg bg-white border border-[#F0EBE3] p-2.5">
            <div className="text-[#9A9A9A] text-[9px] uppercase tracking-widest mb-0.5">Microchip</div>
            <div className="font-mono text-[#1A1A1A] text-[11px]">🔒 Solo vet.</div>
          </div>
          <div className="rounded-lg bg-white border border-[#F0EBE3] p-2.5">
            <div className="text-[#9A9A9A] text-[9px] uppercase tracking-widest mb-0.5">Ultima passeggiata</div>
            <div className="text-[#1A1A1A]">Oggi · 45 min</div>
          </div>
        </div>
        {/* QR row */}
        <div className="mt-3 rounded-xl border border-dashed border-[#E8E1CC] p-3 flex items-center gap-3">
          <div className="h-14 w-14 rounded-lg bg-white grid place-items-center border border-[#F0EBE3]">
            <QrCode className="h-9 w-9 text-[#1A1A1A]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 text-xs">
            <div className="text-[#1A1A1A] font-semibold text-[12px]">Scansiona dal collare</div>
            <div className="text-[#7A7A7A] text-[11px] mt-0.5 leading-snug">Chi trova Luna vede solo i contatti di emergenza, senza login.</div>
          </div>
        </div>
        {/* Community signal — "aiuti attivi vicino a te" */}
        <div className="mt-2.5 rounded-xl border p-2.5 flex items-center gap-2.5"
             style={{ background: "linear-gradient(135deg, #FFF6E6 0%, #FCE8C4 100%)",
                      borderColor: "rgba(255,122,26,0.30)",
                      boxShadow: "0 6px 16px -10px rgba(255,122,26,0.35)" }}>
          <div className="flex -space-x-1.5 shrink-0">
            <div className="h-6 w-6 rounded-full border-2 border-white"
                 style={{ background: "linear-gradient(135deg, #FF7A1A, #F4813C)" }} />
            <div className="h-6 w-6 rounded-full border-2 border-white"
                 style={{ background: "linear-gradient(135deg, #2DBA60, #34D399)" }} />
            <div className="h-6 w-6 rounded-full border-2 border-white flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg, #B24BF3, #8324C9)" }}>
              <span className="text-[9px] font-bold text-white">+7</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-[#1A1A1A] leading-tight">Community attiva a 800m</div>
            <div className="text-[10px] text-[#7A7A7A] leading-tight">10 utenti · 3 segnalazioni oggi</div>
          </div>
          <span className="h-2 w-2 rounded-full bg-[#2DBA60] animate-pulse shrink-0" />
        </div>
      </div>
    </div>
  );
}

function DrawerMockup() {
  const items = [
    { icon: FileLock2, color: "#34D399", title: "Libretto sanitario", sub: "PDF · 2.4 MB", badge: "OK" },
    { icon: Calendar, color: "#FFB020", title: "Vaccino richiamo", sub: "Scade tra 14 giorni", badge: "!" },
    { icon: QrCode, color: "#FF3B30", title: "Passaporto UE", sub: "Aggiornato il 03/01", badge: "OK" },
    { icon: FileLock2, color: "#8b5cf6", title: "Ricevuta vet · Dr. Rossi", sub: "12/02 · 78,00 €", badge: "" },
  ];
  return (
    <div className="relative max-w-sm mx-auto">
      <div aria-hidden className="absolute -inset-8 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(52,211,153,0.25), transparent 70%)" }} />
      <div className="relative rounded-3xl border border-[#F0EBE3] bg-[#FBF7F1] p-5 shadow-2xl">
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A]">Cassetto digitale</div>
          <div className="ml-auto inline-flex items-center gap-1 text-[10px] text-[#34D399]">
            <Shield className="h-2.5 w-2.5" /> CIFRATO
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {items.map((it) => (
            <div key={it.title} className="flex items-center gap-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] px-3 py-2.5">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: `${it.color}1a`, color: it.color }}>
                <it.icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{it.title}</div>
                <div className="text-[11px] text-[#7A7A7A]">{it.sub}</div>
              </div>
              {it.badge && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                  style={{
                    background: it.badge === "!" ? "rgba(255,176,32,0.18)" : "rgba(52,211,153,0.15)",
                    color: it.badge === "!" ? "#FFB020" : "#34D399",
                  }}>
                  {it.badge}
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl border border-[#F0EBE3] bg-[#FAF7F0] px-3 py-2.5 flex items-center gap-2">
          <Bell className="h-3.5 w-3.5 text-[#FFB020]" />
          <span className="text-[11px] text-[#5C5C5C]">Promemoria automatico 7 giorni prima di ogni scadenza</span>
        </div>
        {/* Nearest vet card — one-tap call */}
        <div className="mt-2.5 rounded-xl border p-3 flex items-center gap-3"
             style={{ background: "linear-gradient(135deg, #E8F4EA 0%, #D5EDDD 100%)",
                      borderColor: "rgba(45,186,96,0.30)",
                      boxShadow: "0 6px 16px -10px rgba(45,186,96,0.35)" }}>
          <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
               style={{ background: "linear-gradient(135deg, #34D399, #2DBA60)",
                        boxShadow: "0 4px 10px -3px rgba(45,186,96,0.55)" }}>
            <HeartHandshake className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-[#1E7E3F]">Veterinario più vicino</div>
            <div className="text-[12.5px] font-semibold text-[#1A1A1A] leading-tight mt-0.5 truncate">
              Clinica Dr. Bianchi · 1.2 km
            </div>
            <div className="text-[10px] text-[#5C5C5C] mt-0.5">Aperto ora · via Roma 24</div>
          </div>
          <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
               style={{ background: "#2DBA60", boxShadow: "0 4px 10px -2px rgba(45,186,96,0.55)" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- MATCH IDENTITY MOCKUP (Tinder-style stack: 3 cards visibili) ---------- */
function MatchIdentityMockup() {
  /* Each card gets a slug for the ORIO illustration, a color palette, tag,
     and copy — matching the 3 Match modalities called out in the section:
     1. Passeggiata — trova persona con cane
     2. Area parco — incontri all'aperto
     3. Corsi — con educatori certificati (in arrivo) */
  const cards = [
    {
      slug: "landing-match-stories",
      accent: "#FF7A1A",
      tag: "Stories + Match",
      status: "Live ora",
      name: "Giulia & Rocco",
      sub: "Vizsla · Villa Borghese · storia da 12s",
      badge: { text: "👋 in area", icon: Heart },
      corner: { text: "0,4 km", icon: MapPin },
    },
    {
      slug: "match-walking-v2",
      accent: "#2DBA60",
      tag: "Passeggiata",
      status: "Live oggi",
      name: "Sara & Otto",
      sub: "Border Collie · 3 anni · parco Sempione",
      badge: { text: "92% match", icon: Heart },
      corner: { text: "1,2 km", icon: MapPin },
    },
    {
      slug: "feat-drawer",
      accent: "#5B9EE0",
      tag: "Corsi con educatori",
      status: "In arrivo",
      name: "Elena Bianchi",
      sub: "Educatrice cinofila · certificata FICSS",
      badge: { text: "Prenota", icon: Award },
      corner: { text: "video + pratica", icon: Star },
      comingSoon: true,
    },
  ];

  return (
    <div className="relative max-w-sm mx-auto">
      <div aria-hidden className="absolute -inset-8 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(45,186,96,0.25), transparent 70%)" }} />

      {/* Header pill */}
      <div className="text-center mb-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.22em]"
             style={{ borderColor: "rgba(45,186,96,0.35)", background: "rgba(45,186,96,0.08)", color: "#1E7E3F" }}>
          <span className="h-1.5 w-1.5 rounded-full bg-[#2DBA60] animate-pulse" />
          ORIO Match · Stories · Corsi
        </div>
      </div>

      {/* Card stack — 3 cards fanned like Tinder, all visible.
          Uses explicit absolute positioning so each card occupies a distinct
          region and no single card can cover the others entirely. */}
      <div className="relative w-full mx-auto" style={{ aspectRatio: "1/1.1", maxWidth: 360 }}>
        {cards.map((c, i) => {
          const isTop = i === 0;
          const isMid = i === 1;
          /* Layout:
             - Bottom card (i=2): top-left corner, tilted LEFT, small
             - Middle card (i=1): bottom-right corner, tilted RIGHT
             - Top card (i=0):    centered slightly above, no tilt, largest */
          const layout = isTop
            ? { top: "10%",  left: "18%", width: "64%", height: "80%",
                transform: "rotate(0deg)", zIndex: 30 }
            : isMid
              ? { bottom: "0%", right: "-4%", width: "56%", height: "72%",
                  transform: "rotate(9deg)", zIndex: 20 }
              : { top: "0%",   left: "-4%", width: "56%", height: "72%",
                  transform: "rotate(-9deg)", zIndex: 10 };
          return (
            <motion.div
              key={c.tag}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (2 - i) * 0.12, ease: "easeOut" }}
              className="absolute rounded-3xl overflow-hidden bg-white"
              style={{
                ...layout,
                transformOrigin: "center center",
                border: `1px solid ${isTop ? "#E8E1CC" : "#F0EBE3"}`,
                boxShadow: isTop
                  ? "0 22px 44px -16px rgba(0,0,0,0.30), 0 6px 14px -6px rgba(0,0,0,0.08)"
                  : "0 14px 26px -12px rgba(0,0,0,0.22)",
              }}
            >
              {/* Photo — real ORIO illustration */}
              <IlloImg slug={c.slug} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              {/* Gradient overlay for text legibility */}
              <div className="absolute inset-0"
                   style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.72) 100%)" }} />

              {/* Top badges — smaller on side cards for legibility */}
              <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-1.5">
                <div className={`inline-flex items-center gap-1 rounded-full font-bold text-white ${isTop ? "px-2 py-1 text-[10px]" : "px-1.5 py-0.5 text-[8.5px]"}`}
                     style={{ background: c.accent, boxShadow: `0 4px 10px -2px ${c.accent}77` }}>
                  <c.badge.icon className={isTop ? "h-2.5 w-2.5" : "h-2 w-2"} strokeWidth={2.4} /> {c.badge.text}
                </div>
                <div className={`inline-flex items-center gap-0.5 rounded-full bg-white/95 font-semibold text-[#1A1A1A] ${isTop ? "px-2 py-1 text-[10px]" : "px-1.5 py-0.5 text-[8.5px]"}`}>
                  <c.corner.icon className={isTop ? "h-2.5 w-2.5" : "h-2 w-2"} /> {c.corner.text}
                </div>
              </div>

              {/* Coming Soon overlay */}
              {c.comingSoon && (
                <div className={`absolute right-2 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-widest text-white ${isTop ? "top-14 text-[9px]" : "top-10 text-[8px]"}`}
                     style={{ background: "linear-gradient(135deg,#FF7A1A,#FF5C2A)" }}>
                  In arrivo
                </div>
              )}

              {/* Bottom info — compact on side cards */}
              <div className="absolute inset-x-2 bottom-2 text-white">
                <div className={`inline-flex items-center gap-1 uppercase tracking-[0.20em] font-bold ${isTop ? "text-[9px]" : "text-[7.5px]"}`}
                     style={{ color: c.accent === "#FF7A1A" ? "#FFD59E" : "#FFFFFFCC" }}>
                  <span className="h-1 w-1 rounded-full" style={{ background: c.accent === "#FF7A1A" ? "#FFB020" : "#8FE7C1" }} />
                  {c.tag} · {c.status}
                </div>
                <div className={`font-display leading-tight mt-1 tracking-tight ${isTop ? "text-[18px]" : "text-[13px]"}`}>{c.name}</div>
                <div className={`text-white/90 leading-tight mt-0.5 ${isTop ? "text-[11px]" : "text-[9px]"}`}>{c.sub}</div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Swipe hint under stack */}
      <div className="mt-4 flex items-center justify-center gap-3">
        <div className="h-8 w-8 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center"
             style={{ boxShadow: "0 4px 10px -4px rgba(0,0,0,0.15)" }}>
          <X className="h-3.5 w-3.5 text-[#7A7A7A]" />
        </div>
        <div className="h-11 w-11 rounded-full flex items-center justify-center text-white"
             style={{ background: "linear-gradient(135deg,#2DBA60,#34D399)",
                      boxShadow: "0 8px 16px -4px rgba(45,186,96,0.55)" }}>
          <Heart className="h-5 w-5 fill-white" />
        </div>
        <div className="h-8 w-8 rounded-full flex items-center justify-center text-white"
             style={{ background: "linear-gradient(135deg,#7B61FF,#5B44E0)" }}>
          <Star className="h-3.5 w-3.5 fill-white" />
        </div>
      </div>
    </div>
  );
}

/* ---------- CASSETTO ---------- */
/* ---------- FEATURE GALLERY (Trade Republic style — vertical mobile, horizontal desktop) ---------- */
const GALLERY_IMG_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/static/illustrations`;

const GALLERY_FEATURES = [
  {
    slug: "feat-radar",
    fallback: "feat-radar.png",
    icon: Radar,
    color: "#C25612",
    tag: "ORIO Radar · Nuovo",
    title: "Il radar sicurezza del tuo cane.",
    desc: "Incendi in tempo reale (EFFIS Copernicus), allerte meteo (Protezione Civile) e zone endemiche (leishmaniosi, filaria, zecche). Tutto sulla mappa, tutto verificato.",
    highlight: "Copernicus • Protezione Civile",
  },
  {
    slug: "gallery-walk",
    fallback: "feat-walk.png",
    icon: Footprints,
    color: "#34D399",
    tag: "ORIO Walk",
    title: "Ogni passeggiata, misurata.",
    desc: "Distanza, ritmo, streak settimanali e karma guadagnato ad ogni uscita. Come Apple Fitness, ma per il tuo cane.",
    highlight: "Traccia • Streak • Karma",
  },
  {
    slug: "gallery-hazards",
    fallback: "tip-hazard.png",
    icon: AlertTriangle,
    color: "#FF7A1A",
    tag: "Hazards",
    title: "Sai cosa evitare, prima di uscire.",
    desc: "Cantieri, esche, veleni segnalati dalla community sulla mappa. Sempre aggiornati, sempre verificati.",
    highlight: "Mappa live • Community",
  },
  {
    slug: "gallery-temperature",
    fallback: "weather-hot.png",
    icon: Thermometer,
    color: "#F4B83C",
    tag: "Temperatura",
    title: "L'asfalto è troppo caldo?",
    desc: "ORIO ti avvisa quando la temperatura al suolo supera la soglia di sicurezza per le zampe del tuo cane.",
    highlight: "Alert meteo • Zampe safe",
  },
  {
    slug: "gallery-match",
    fallback: "feat-match.png",
    icon: HeartHandshake,
    color: "#5B9EE0",
    tag: "Match",
    title: "Trova compagni di passeggiata.",
    desc: "Match tra cani compatibili per età, taglia e temperamento. Educatori e parchi verificati nella tua zona.",
    highlight: "Cani • Educatori • Parchi",
  },
  {
    slug: "gallery-karma",
    fallback: "feat-karma-v2.png",
    icon: Award,
    color: "#FFB020",
    tag: "Karma",
    title: "La gentilezza vale davvero.",
    desc: "Aiuti la community, guadagni Karma. Sali di livello: da Cittadino a Custode. Riconoscimento, non punti fittizi.",
    highlight: "5 livelli • Verificato",
  },
  {
    slug: "gallery-notifications",
    fallback: "tip-pro.png",
    icon: Bell,
    color: "#7B61FF",
    tag: "Notifiche",
    title: "Le cose che contano, al momento giusto.",
    desc: "SOS entro 80km, promemoria vaccini 7 giorni prima, alert meteo, missioni vicino a te. Silenzio sul resto.",
    highlight: "Push • Zero spam",
  },
  {
    slug: "gallery-drawer",
    fallback: "feat-drawer.png",
    icon: FileLock2,
    color: "#2DBA60",
    tag: "Cassetto",
    title: "Tutti i documenti, sempre con te.",
    desc: "Vaccinazioni, pedigree, ricevute vet. Cifrati lato server, accessibili offline. Il libretto che non perdi mai.",
    highlight: "Cifrato • Offline",
  },
];

function FeatureGallery() {
  const navigate = useNavigate();
  return (
    <section id="gallery" className="relative z-10 py-20 sm:py-28 overflow-hidden">
      {/* Soft warm halo behind gallery */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute top-10 right-[8%] w-[520px] h-[520px] rounded-full opacity-40"
          style={{ background: "radial-gradient(circle, rgba(52,211,153,0.22) 0%, transparent 70%)", filter: "blur(70px)" }} />
        <div className="absolute bottom-20 left-[5%] w-[560px] h-[560px] rounded-full opacity-35"
          style={{ background: "radial-gradient(circle, rgba(255,176,32,0.22) 0%, transparent 70%)", filter: "blur(70px)" }} />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative">
        {/* Section header */}
        <div className="max-w-2xl mb-10 sm:mb-14">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] mb-3">Tutto quello che ORIO fa</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.12] pb-1">
            Gli strumenti quotidiani<br />
            <span className="orio-grad-text">del tuo cane, in un solo ecosistema.</span>
          </h2>
          <p className="mt-5 text-[#5C5C5C] text-sm sm:text-base leading-relaxed max-w-lg">
            ORIO riunisce tutte le funzioni essenziali che ti servono ogni giorno:
            radar sicurezza, community operativa, cassetto sanitario, veterinari vicini, match, passeggiate.
            Un&apos;unica piattaforma italiana, tutta parlata dalla stessa mano.
          </p>
        </div>

        {/* Horizontal snap-carousel — one card at a time on mobile, multiple visible on desktop */}
        <div
          data-testid="feature-gallery"
          className="
            flex flex-row gap-4 overflow-x-auto snap-x snap-mandatory
            -mx-6 px-6 pb-4
            [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            sm:gap-5 sm:pb-4
          "
          style={{ scrollPaddingLeft: "24px", scrollPaddingRight: "24px" }}
        >
          {GALLERY_FEATURES.map((f, i) => (
            <FeatureCard key={f.slug} feature={f} index={i} />
          ))}

          {/* Final CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="
              relative shrink-0 snap-start snap-always
              w-[80vw] max-w-[320px] md:w-[320px]
              rounded-3xl overflow-hidden
              border border-[#F0EBE3]
              flex flex-col
            "
            style={{
              background: "linear-gradient(155deg, #FF7A1A 0%, #F4B83C 55%, #FFB020 100%)",
              boxShadow: "0 20px 60px -25px rgba(255,122,26,0.55), 0 6px 18px -6px rgba(0,0,0,0.08)",
            }}
          >
            <div className="p-6 sm:p-7 flex flex-col justify-between min-h-[380px] md:min-h-[520px] text-white">
              <div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/25 backdrop-blur text-[10px] uppercase tracking-[0.16em] font-medium">
                  <Sparkles className="h-3 w-3" />
                  Prova ORIO
                </div>
                <h3 className="mt-4 font-display text-2xl sm:text-3xl leading-tight">
                  30 giorni gratis.<br />
                  Senza carta.
                </h3>
                <p className="mt-3 text-white/90 text-sm leading-relaxed">
                  Attiva la Pro con un tap. Nessun addebito nei primi 30 giorni.
                  Cancelli quando vuoi, senza penali.
                </p>
              </div>
              <button
                onClick={() => { trackInstallEvent("register_opened"); navigate("/register"); }}
                data-testid="gallery-cta-btn"
                className="mt-6 group inline-flex items-center justify-between gap-3 w-full px-5 py-3.5 rounded-2xl bg-white text-[#1A1A1A] font-medium text-sm hover:shadow-xl transition-all"
              >
                <span>Inizia i 30 giorni gratis</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="mt-3 text-[11px] text-white/80 flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" />
                Nessuna carta richiesta all'attivazione
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll hint — visible on all sizes */}
        <div className="flex items-center gap-2 mt-4 sm:mt-6 text-[11px] uppercase tracking-[0.18em] text-[#9A9A9A]">
          <span>Scorri</span>
          <div className="h-px w-16 bg-[#E5DFD5]" />
          <ChevronRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature, index }) {
  const { slug, fallback, icon: Icon, color, tag, title, desc, highlight } = feature;
  const [imgErr, setImgErr] = useState(false);
  const src = imgErr
    ? `${GALLERY_IMG_BASE}/${fallback}?v=28`
    : `${GALLERY_IMG_BASE}/${slug}.png?v=28`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: index * 0.05 }}
      data-testid={`feature-card-${slug}`}
      className="
        group relative shrink-0 snap-start snap-always
        w-[80vw] max-w-[320px] md:w-[320px]
        rounded-3xl overflow-hidden
        bg-white border border-[#F0EBE3]
        flex flex-col
        transition-all duration-300
        hover:border-transparent hover:-translate-y-1
      "
      style={{ boxShadow: "0 10px 30px -15px rgba(0,0,0,0.08), 0 3px 10px -4px rgba(0,0,0,0.04)" }}
    >
      {/* Image (portrait aspect) */}
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#F5EFE6]">
        <img
          src={src}
          onError={() => setImgErr(true)}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
        />
        {/* Bottom fade for readability of tag */}
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent" />
        {/* Tag chip bottom-left */}
        <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur">
          <Icon className="h-3.5 w-3.5" style={{ color }} />
          <span className="text-[11px] font-medium tracking-tight text-[#1A1A1A]">{tag}</span>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        <h3 className="font-display text-lg sm:text-xl leading-snug text-[#1A1A1A]">
          {title}
        </h3>
        <p className="mt-2 text-sm text-[#5C5C5C] leading-relaxed flex-1">{desc}</p>
        <div className="mt-4 pt-4 border-t border-[#F0EBE3] flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-[0.14em] text-[#9A9A9A]">{highlight}</span>
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-full transition-all group-hover:scale-110"
            style={{ backgroundColor: `${color}18` }}
          >
            <ChevronRight className="h-3.5 w-3.5" style={{ color }} />
          </span>
        </div>
      </div>
    </motion.article>
  );
}

/* ---------- FAQ ---------- */
function FAQBlock() {
  const items = [
    {
      q: "Come scarico ORIO su Android e iPhone?",
      a: "Su Android scarichi ORIO gratis dal Google Play Store (cerca \"Oriodog\" o tocca il pulsante Google Play qui sopra). Su iPhone non serve l'App Store: apri oriodog.com con Safari, tocca il tasto Condividi e poi \"Aggiungi alla schermata Home\". In 5 secondi hai l'icona ORIO con notifiche e accesso rapido, esattamente come un'app tradizionale.",
    },
    {
      q: "Cosa fa esattamente ORIO?",
      a: "ORIO è un'infrastruttura di sicurezza e gestione per il mondo canino. Sulla mappa vedi in tempo reale ORIO Radar (incendi ufficiali NASA/Copernicus, allerte meteo Protezione Civile, zone endemiche zecche/leishmaniosi, caldo estremo sull'asfalto). Crei il passaporto digitale con QR code, tieni tutti i documenti sanitari nel Cassetto, trovi il veterinario aperto più vicino, partecipi a missioni di soccorso e organizzi passeggiate con utenti compatibili nel Match. Non vendiamo hardware: siamo l'ecosistema che mette in rete persone, rifugi e attivisti attorno alla salvezza dei cani.",
    },
    {
      q: "Cosa è gratis e cosa è Pro?",
      a: "GRATIS PER SEMPRE: ORIO Radar completo (incendi, meteo, zecche, caldo, bocconi), mappa pericoli con segnalazione tap-to-report, community e missioni base, cassetto essenziale (fino a 5 documenti), veterinari vicini con chiamata diretta, profilo pubblico del cane con QR. PRO (4,99 €/mese, prova 30 giorni SENZA carta): SOS emergenza con push a tutti gli utenti nel raggio, Match illimitato, ORIO Walk (Apple Fitness-style), cassetto illimitato con backup, notifiche push per zone endemiche vicino a te.",
    },
    {
      q: "Devo davvero inserire la carta per la prova Pro?",
      a: "No. Puoi provare tutte le funzioni Pro per 30 giorni senza inserire alcun metodo di pagamento. Alla fine dei 30 giorni, se non fai nulla, torni automaticamente al piano Free. Se vuoi continuare con Pro, aggiungi la carta e paghi 4,99 €/mese. Zero sorprese, zero addebiti automatici senza il tuo consenso esplicito.",
    },
    {
      q: "Come funziona il pulsante SOS Emergenza?",
      a: "Il pulsante SOS è nel piano Pro. Premi il tasto SOS, confermi l'ultima posizione nota e i dettagli. ORIO pubblica istantaneamente la scheda del cane sulla mappa nazionale e invia una notifica push a tutti gli utenti registrati entro un raggio che cresce da 10 a 80 km col passare delle ore. Chi ha installato ORIO sullo smartphone riceve l'avviso anche con app chiusa. Quando il cane è in salvo, chiudi l'emergenza con un click.",
    },
    {
      q: "Cosa vede ORIO Radar in tempo reale?",
      a: "Il Radar aggrega dati pubblici ufficiali: incendi rilevati dai satelliti NASA MODIS (ultimi 24h), overlay incendi europei EFFIS Copernicus, bollettini criticità Protezione Civile italiana (idraulico, idrogeologico, temporali), temperature percepite Open-Meteo per 30 città italiane con soglie caldo asfalto, zone endemiche leishmaniosi/filaria/zecche da studi ISS e veterinari. Tutto gratis, tutto verificato, tutto sotto la tua mappa.",
    },
    {
      q: "Devo già avere un cane per iscrivermi?",
      a: "No. Puoi iscriverti anche per partecipare alle Missioni come volontario, dare disponibilità per stalli temporanei, o semplicemente per essere parte della rete di sentinelle che ricevono le emergenze nella propria zona. Il passaporto digitale e il Match ovviamente richiedono almeno un cane.",
    },
    {
      q: "Cosa sono i punti Karma ORIO?",
      a: "I punti Karma riconoscono la tua affidabilità nella rete: li guadagni partecipando alle missioni, confermando avvistamenti con foto e coordinate, e portando a buon fine i salvataggi. I punti rimangono in stato 'in attesa' per 48 ore prima di diventare definitivi — questo previene chiusure fittizie. I livelli salgono da Cittadino a Soccorritore, Guardiano, Sentinella ORIO e Custode.",
    },
    {
      q: "ORIO sostituisce microchip e libretto sanitario?",
      a: "No. Il microchip resta l'identificazione legale obbligatoria del tuo cane, e il libretto cartaceo conserva il suo valore. ORIO è complementare: digitalizza e mette in rete queste informazioni perché siano subito accessibili in emergenza, a veterinari di guardia, a chi trova il cane scansionando il QR.",
    },
    {
      q: "Posso annullare l'abbonamento Pro quando voglio?",
      a: "Sì, in ogni momento dall'area Profilo. Niente vincoli, niente penali. Il periodo già pagato resta attivo fino alla scadenza naturale. Le tue informazioni — passaporti, documenti, missioni — restano nel tuo account anche se torni al piano Free.",
    },
  ];
  const [open, setOpen] = useState(-1);
  return (
    <section id="faq" className="relative z-10 py-20 sm:py-28  overflow-hidden">
      {/* Warm color halo behind FAQ */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 left-[10%] w-[420px] h-[420px] rounded-full opacity-50"
          style={{ background: "radial-gradient(circle, rgba(255,176,32,0.22) 0%, transparent 70%)", filter: "blur(60px)" }} />
        <div className="absolute bottom-0 right-[5%] w-[480px] h-[480px] rounded-full opacity-45"
          style={{ background: "radial-gradient(circle, rgba(91,158,224,0.20) 0%, rgba(45,186,96,0.14) 50%, transparent 80%)", filter: "blur(70px)" }} />
      </div>
      <div className="max-w-3xl mx-auto px-6 relative">
        <div className="text-center mb-10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] mb-3">Le domande che contano</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.12] pb-1">
            Risposte dirette,<br />
            <span className="orio-grad-text">niente giri di parole.</span>
          </h2>
        </div>
        <div className="space-y-3">
          {items.map((it, i) => (
            <button
              key={i}
              onClick={() => setOpen(open === i ? -1 : i)}
              data-testid={`faq-${i}`}
              className="w-full text-left bg-white/85 backdrop-blur-md border border-[#F0EBE3] rounded-2xl p-5 hover:bg-white hover:border-[#FFB020]/40 transition-all"
              style={{ boxShadow: "0 8px 24px -10px rgba(0,0,0,0.06)" }}
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-display text-lg sm:text-xl">{it.q}</h3>
                <ChevronDown className={`h-5 w-5 text-[#9A9A9A] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </div>
              <motion.div
                initial={false}
                animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
                transition={{ duration: 0.3 }}
                style={{ overflow: "hidden" }}
              >
                <p className="mt-4 text-[#5C5C5C] leading-relaxed text-sm sm:text-base">{it.a}</p>
              </motion.div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- PRICING ---------- */
function PricingBlock() {
  return (
    <section id="prezzo" className="relative z-10 py-20 sm:py-28  overflow-hidden">
      {/* Soft brand gradient halo behind the pricing card */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 -translate-x-1/2 top-1/3 w-[700px] h-[700px] rounded-full opacity-50"
          style={{ background: "radial-gradient(circle, rgba(255,176,32,0.25) 0%, rgba(52,211,153,0.18) 45%, rgba(255,59,48,0.10) 75%, transparent 90%)", filter: "blur(40px)" }} />
      </div>
      <div className="max-w-2xl mx-auto px-6 text-center relative">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] mb-3">Il piano</div>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.12] pb-1">
          Gratis <span className="orio-grad-text">davvero</span>.<br />
          Pro? <span className="orio-grad-text">30 giorni senza carta</span>.
        </h2>
        <p className="mt-5 text-[#5C5C5C] text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
          ORIO ha molte funzioni sempre gratuite: <strong className="text-[#1A1A1A]">mappa pericoli, ORIO Radar, community, cassetto essenziale, veterinari vicini</strong>.
          Se vuoi anche l&apos;SOS emergenza, Match illimitato, ORIO Walk e backup, provi il piano Pro <strong className="text-[#1A1A1A]">30 giorni gratis · nessuna carta richiesta</strong>. Se ti piace, poi 4,99 €/mese. Se no, torni al Free senza fare nulla.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11.5px] font-semibold"
             style={{ background: "rgba(45,186,96,0.12)", color: "#1E7E3F", border: "1px solid rgba(45,186,96,0.25)" }}>
          <span className="h-1.5 w-1.5 rounded-full bg-[#2DBA60] animate-pulse" />
          Zero pubblicità · Zero vendita di dati · Piattaforma indipendente italiana
        </div>
      </div>

      {/* PLAN COMPARISON (Free vs Pro) — full feature matrix with animated reveals */}
      <div className="max-w-3xl mx-auto px-6 mt-10 relative">
        <PlanComparison variant="landing" onSelectPro={() => { trackInstallEvent("register_opened"); window.location.href = "/register"; }} />
      </div>

      <div className="max-w-2xl mx-auto px-6 text-center relative" style={{ display: "none" }}>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] mb-3 hidden">Abbonamento</div>

        {/* ORIO-style sparkly card: animated gradient border + warm fill */}
        <div className="relative mt-10 group">
          {/* Animated gradient border */}
          <div
            aria-hidden
            className="absolute -inset-[2px] rounded-[28px] opacity-90"
            style={{
              background: "linear-gradient(120deg, #FF3B30 0%, #FF7A1A 25%, #FFB020 50%, #2DBA60 75%, #5B9EE0 100%)",
              backgroundSize: "300% 300%",
              animation: "orio-bg-shift 9s ease infinite",
              filter: "blur(0.5px)",
            }}
          />
          {/* Halo glow */}
          <div
            aria-hidden
            className="absolute -inset-6 rounded-[36px] opacity-60 pointer-events-none"
            style={{
              background: "radial-gradient(60% 70% at 50% 50%, rgba(255,176,32,0.40) 0%, rgba(52,211,153,0.20) 50%, transparent 80%)",
              filter: "blur(20px)",
            }}
          />
          {/* Sparkles */}
          <Sparkles className="absolute -top-3 -right-3 h-6 w-6 text-[#FFB020] drop-shadow-[0_0_8px_rgba(255,176,32,0.7)] animate-pulse" />
          <Sparkles className="absolute -bottom-3 -left-3 h-5 w-5 text-[#FF7A1A] drop-shadow-[0_0_8px_rgba(255,122,26,0.7)] animate-pulse" style={{ animationDelay: "0.8s" }} />

          {/* Inner card */}
          <div className="relative rounded-[26px] p-8 sm:p-10 text-left"
            style={{
              background: "linear-gradient(160deg, #FFFCF6 0%, #FBF7F1 100%)",
              boxShadow: "0 30px 60px -20px rgba(255,122,26,0.25), 0 10px 30px -10px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)",
            }}
          >
            {/* Gold-foil "PIÙ POPOLARE" ribbon */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3.5 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-bold text-white shadow-lg"
              style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 100%)", boxShadow: "0 8px 20px -4px rgba(255,122,26,0.55)" }}>
              ★ Tutto incluso
            </div>

            <div className="flex items-baseline justify-between flex-wrap gap-4">
              <div>
                <div className="font-display text-5xl sm:text-6xl orio-grad-text">4,99 €</div>
                <div className="text-[#6B6B6B] text-sm mt-1">al mese · IVA inclusa</div>
              </div>
              <div className="text-right">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                  style={{ background: "rgba(45,186,96,0.15)", color: "#1E7E3F", border: "1px solid rgba(45,186,96,0.35)" }}>
                  <span className="h-1.5 w-1.5 rounded-full bg-[#2DBA60]" />
                  30 giorni gratis
                </div>
                <div className="text-[#7A7A7A] text-xs mt-1.5">Disdici quando vuoi</div>
              </div>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 gap-3 text-sm">
              {[
                { t: "Passaporto digitale + QR", c: "#FF7A1A" },
                { t: "Mappa nazionale 24/7", c: "#5B9EE0" },
                { t: "Tasto emergenza geolocalizzata", c: "#FF3B30" },
                { t: "Missioni & match community", c: "#2DBA60" },
                { t: "Cassetto digitale cifrato", c: "#8B5CF6" },
                { t: "Notifiche scadenze sanitarie", c: "#FFB020" },
              ].map((b) => (
                <div key={b.t} className="flex items-start gap-2 text-[#1A1A1A]">
                  <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ background: b.c, boxShadow: `0 0 8px ${b.c}99` }} />
                  {b.t}
                </div>
              ))}
            </div>
            <Link
              to="/register"
              data-testid="pricing-cta"
              onClick={() => trackInstallEvent("register_opened")}
              className="mt-8 inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-full text-white font-semibold relative overflow-hidden transition-transform active:scale-[0.98]"
              style={{
                background: "linear-gradient(135deg, #FF3B30 0%, #FF7A1A 50%, #FFB020 100%)",
                backgroundSize: "200% 200%",
                animation: "orio-bg-shift 6s ease infinite",
                boxShadow: "0 14px 32px -8px rgba(255,122,26,0.55)",
              }}
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                Inizia i 30 giorni gratuiti <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- FOOTER ---------- */
function Footer() {
  const { user } = useAuth();
  return (
    <footer className="relative z-10  py-16 overflow-hidden">
      <PawPrint className="absolute top-6 left-1/4 h-8 w-8 text-[#FF7A1A]/10 rotate-[10deg] pointer-events-none" />
      <PawPrint className="absolute bottom-6 right-1/4 h-10 w-10 text-[#F4B83C]/10 rotate-[-18deg] pointer-events-none" />
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight"
        >
          ORIO non è un'app.<br />
          <span className="orio-grad-text">È un patto tra chi ama i cani.</span>
        </motion.p>

        {/* Mobile-prominent install CTA at the very bottom of the landing */}
        <div className="mt-8 flex justify-center md:hidden" data-testid="footer-install-cta">
          <InstallPrompt variant="inline" />
        </div>

        <div className="mt-12 flex items-center justify-center gap-3 text-[#9A9A9A] text-sm">
          <div aria-hidden className="h-6 w-6 orio-logo-grad opacity-80" />
          <span>© {new Date().getFullYear()} ORIO · Infrastruttura italiana per il mondo canino</span>
        </div>
        <div className="mt-4 flex items-center justify-center gap-6 text-sm text-[#9A9A9A]">
          {user ? (
            <Link to="/app" data-testid="footer-goto-app" className="hover:text-[#FF7A1A] inline-flex items-center gap-1.5">
              Vai all&apos;app <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <>
              <Link to="/login" className="hover:text-[#FF7A1A]">Accedi</Link>
              <Link to="/register" onClick={() => trackInstallEvent("register_opened")} className="hover:text-[#FF7A1A]">Registrati</Link>
            </>
          )}
        </div>

        {/* ───── VIXEL attribution ───── */}
        <div className="mt-14 pt-8 border-t border-[#F0EBE3] max-w-2xl mx-auto" data-testid="footer-vixel-attribution">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#9A9A9A] mb-3">
            Un servizio offerto da
          </div>
          <a
            href="https://vixelgraphic.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#1A1A1A] hover:text-[#FF7A1A] transition-colors group"
            data-testid="footer-vixel-link"
          >
            <VixelMark height={22} className="text-[#1A1A1A] group-hover:text-[#FF7A1A] transition-colors" />
            <span className="text-[12px] text-[#9A9A9A] group-hover:text-[#FF7A1A]">vixelgraphic.com</span>
          </a>
          <p className="mt-3 text-[11px] leading-relaxed text-[#9A9A9A] max-w-md mx-auto">
            ORIO è un marchio e servizio di <strong className="text-[#6B6B6B]">Vixel</strong> — studio italiano di design e infrastruttura digitale.
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-[#9A9A9A]">
            <span data-testid="footer-piva">P.IVA <strong className="text-[#6B6B6B] tabular-nums">18531031005</strong></span>
            <span aria-hidden className="h-1 w-1 rounded-full bg-[#E5DECF]" />
            <a href="mailto:vixel@pec.it" className="hover:text-[#FF7A1A]" data-testid="footer-pec">
              PEC <strong className="text-[#6B6B6B]">vixel@pec.it</strong>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ---------- INSTALL APP (PWA) ---------- */
function InstallAppBlock() {
  return (
    <section className="relative z-10 py-20 sm:py-28 ">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] mb-4">Scarica ORIO</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.12] pb-1">
            Android e iPhone.<br />
            <span className="orio-grad-text">In pochi secondi.</span>
          </h2>
          <p className="mt-5 text-[#5C5C5C] leading-relaxed text-sm sm:text-base">
            Su <strong>Android</strong> ORIO è sul <strong>Google Play Store</strong>: la scarichi come qualsiasi app.
            Su <strong>iPhone</strong> non serve l&apos;App Store: apri <strong>oriodog.com</strong> con Safari, tocca <em>Condividi → Aggiungi alla schermata Home</em> e ORIO diventa un&apos;icona come tutte le altre, con notifiche, passaporto offline e SOS a portata di pollice.
          </p>
          <div className="mt-6 space-y-2.5 text-sm">
            <div className="flex items-center gap-2.5 text-[#1A1A1A]">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#34D399" }} />
              Android: scarica da Google Play
            </div>
            <div className="flex items-center gap-2.5 text-[#1A1A1A]">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#5B9EE0" }} />
              iPhone: installa dal sito in 5 secondi
            </div>
            <div className="flex items-center gap-2.5 text-[#1A1A1A]">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#FFB020" }} />
              Notifiche su Radar, SOS e pericoli anche ad app chiusa
            </div>
            <div className="flex items-center gap-2.5 text-[#1A1A1A]">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#FF3B30" }} />
              SOS Emergenza con un tap, sempre visibile
            </div>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#FAF7F0] border border-[#F0EBE3] text-xs text-[#7A7A7A]">
            Gratis · si aggiorna da sola · 30 giorni Pro inclusi
          </div>
          {/* Desktop install CTA — sits next to the mockup */}
          <div className="mt-6 hidden md:block" data-testid="install-cta-row">
            <InstallPrompt variant="inline" />
          </div>
        </div>
        <div className="flex items-center justify-center">
          <InstallPhoneMock />
        </div>
      </div>

      {/* Mobile install CTA — large, prominent, AFTER the mockup so it's the clear final action */}
      <div className="md:hidden max-w-6xl mx-auto px-6 mt-10" data-testid="install-cta-mobile">
        <InstallPrompt variant="inline-mobile" />
        <p className="mt-3 text-center text-[11px] text-[#9A9A9A] leading-relaxed">
          Tocca qui sopra per ricevere le istruzioni passo passo per il tuo telefono.
        </p>
      </div>
    </section>
  );
}

function InstallPhoneMock() {
  return <DynamicPhoneMock />;
}

/* ---------- DYNAMIC PHONE MOCK ---------- 
   Loops through 4 scenes: Mappa → Radar → Match → Walk.
   Each scene is a mini fully-designed screen with subtle motion.
   Larger frame (280x580) so text is readable; notch clearly separated from content. */
function DynamicPhoneMock() {
  const [scene, setScene] = useState(0);
  const total = 4;
  useEffect(() => {
    const t = setInterval(() => setScene((s) => (s + 1) % total), 3800);
    return () => clearInterval(t);
  }, []);
  const labels = ["Mappa", "Radar", "Match", "Walk"];
  return (
    <div className="relative w-[260px] h-[540px] sm:w-[300px] sm:h-[615px] rounded-[46px] bg-gradient-to-b from-[#1f1f1f] to-[#0a0a0a] p-[6px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)] border border-[#F0EBE3]"
         data-testid="dynamic-phone-mock">
      <div aria-hidden className="absolute -inset-6 -z-10 opacity-50"
        style={{ background: "radial-gradient(60% 70% at 50% 50%, rgba(255,176,32,0.25) 0%, rgba(52,211,153,0.15) 50%, rgba(0,0,0,0) 75%)" }} />
      <div className="relative w-full h-full rounded-[40px] overflow-hidden bg-[#FBF7F1]">
        {/* Dynamic Island style — small pill, sits at top center */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-[18px] rounded-full bg-[#0A0A0A] z-30" />
        {/* Progress dots — well below the notch */}
        <div className="absolute top-[34px] left-1/2 -translate-x-1/2 z-20 flex items-center gap-1">
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className="h-1 rounded-full transition-all"
                  style={{
                    width: scene === i ? 18 : 5,
                    background: scene === i ? "#FF7A1A" : "rgba(180,166,135,0.35)",
                  }} />
          ))}
        </div>
        {/* Scene stage — starts safely below notch + dots */}
        <div className="absolute inset-x-0 top-[48px] bottom-[52px]">
          <motion.div key={`scene-${scene}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0">
            {scene === 0 && <SceneMappa />}
            {scene === 1 && <SceneRadar />}
            {scene === 2 && <SceneMatch />}
            {scene === 3 && <SceneWalk />}
          </motion.div>
        </div>
        {/* Bottom nav — pinned, matches the real PWA */}
        <div className="absolute inset-x-0 bottom-0 z-10 h-[52px] border-t border-[#F0EBE3] bg-white/90 backdrop-blur px-2 py-1.5 flex items-center justify-around">
          {[Home, MapPin, Heart, Footprints].map((Ic, i) => {
            const active = scene === i;
            return (
              <div key={i} className="flex flex-col items-center gap-0.5 transition-all"
                   style={{ transform: active ? "scale(1.12)" : "scale(1)" }}>
                <Ic className="h-4 w-4" style={{ color: active ? "#FF7A1A" : "#9A9A9A" }} strokeWidth={active ? 2.6 : 2} />
                <span className="text-[8.5px] font-semibold" style={{ color: active ? "#FF7A1A" : "#9A9A9A" }}>
                  {labels[i]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* --- Scene 1: Mappa (missioni + hazards) --- */
function SceneMappa() {
  return (
    <div className="absolute inset-0 px-3 py-2 text-[#1A1A1A]">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-[8.5px] uppercase tracking-widest text-[#9A9A9A]">Mappa · Milano</div>
          <div className="font-display text-[16px] leading-tight">La tua rete oggi</div>
        </div>
        <span className="px-2 py-0.5 rounded-full bg-[#2DBA60]/12 text-[#2DBA60] text-[9px] font-bold whitespace-nowrap">4 vicino</span>
      </div>
      {/* Map canvas */}
      <div className="relative h-[calc(100%-32px)] rounded-2xl bg-[#F5EFE6] border border-[#F0EBE3] overflow-hidden">
        <svg viewBox="0 0 200 250" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
          <rect width="200" height="250" fill="#F4EFE6" />
          <path d="M -10 60 Q 60 40 120 70 T 220 55" stroke="#B8CFA5" strokeWidth="8" fill="none" opacity="0.55" />
          <path d="M -10 150 Q 80 130 150 165 T 220 140" stroke="#C7D8B9" strokeWidth="6" fill="none" opacity="0.55" />
          <path d="M -10 200 L 220 205" stroke="#F0E2CE" strokeWidth="4" fill="none" opacity="0.7" />
          <path d="M 50 -10 L 55 260" stroke="#F0E2CE" strokeWidth="3" opacity="0.55" />
          <path d="M 130 -10 L 138 260" stroke="#F0E2CE" strokeWidth="3" opacity="0.55" />
          <ellipse cx="60" cy="90" rx="26" ry="16" fill="#C2D6A6" opacity="0.55" />
          <ellipse cx="150" cy="180" rx="32" ry="20" fill="#C2D6A6" opacity="0.55" />
        </svg>
        {/* You marker */}
        <div className="absolute" style={{ top: "50%", left: "50%", transform: "translate(-50%,-50%)" }}>
          <span className="absolute inset-0 h-4 w-4 rounded-full bg-[#FF7A1A] orio-marker-ping" />
          <span className="relative block h-4 w-4 rounded-full bg-[#FF7A1A] border-[2.5px] border-white shadow-[0_0_12px_#FF7A1A]" />
        </div>
        {/* Community pins */}
        {[
          { top: "22%", left: "28%", c: "#2DBA60" },
          { top: "70%", left: "72%", c: "#2DBA60" },
          { top: "32%", left: "78%", c: "#FFB020" },
          { top: "78%", left: "22%", c: "#FF3B30" },
        ].map((p, i) => (
          <div key={i} className="absolute h-3 w-3 rounded-full border-2 border-white"
               style={{ top: p.top, left: p.left, background: p.c, boxShadow: `0 0 8px ${p.c}` }} />
        ))}
        {/* Bottom info card */}
        <div className="absolute inset-x-2 bottom-2 rounded-xl bg-white/95 backdrop-blur border border-[#F0EBE3] p-2 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center"
               style={{ background: "rgba(255,122,26,0.14)", color: "#FF7A1A" }}>
            <HeartHandshake className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-semibold leading-tight truncate">Cane smarrito · 1,8 km</div>
            <div className="text-[9px] text-[#7A7A7A] leading-tight">Bassotto marrone · 12 min fa</div>
          </div>
          <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold text-white"
                style={{ background: "linear-gradient(135deg,#FF3B30,#FF7A1A)" }}>SOS</span>
        </div>
      </div>
    </div>
  );
}

/* --- Scene 2: Radar (live alert card sliding in) --- */
function SceneRadar() {
  return (
    <div className="absolute inset-0 px-3 py-2 text-[#1A1A1A]">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-[8.5px] uppercase tracking-widest" style={{ color: "#a78bfa" }}>ORIO Radar · Live</div>
          <div className="font-display text-[16px] leading-tight">Cosa succede intorno</div>
        </div>
        <span className="h-2 w-2 rounded-full bg-[#FF3B30] animate-pulse" />
      </div>
      {/* Radar canvas */}
      <div className="relative h-[calc(100%-32px)] rounded-2xl overflow-hidden border border-[#F0EBE3]"
           style={{ background: "radial-gradient(60% 60% at 50% 50%, #FFF8EE 0%, #F4EFE6 100%)" }}>
        <svg viewBox="0 0 200 250" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
          {[42, 78, 114].map((r, i) => (
            <circle key={i} cx="100" cy="115" r={r} fill="none" stroke="#FF7A1A" strokeWidth="0.8" opacity={0.20 + i * 0.05}>
              <animate attributeName="r" values={`${r};${r + 7};${r}`} dur="3s" repeatCount="indefinite" begin={`${i * 0.4}s`} />
              <animate attributeName="opacity" values={`${0.25 + i * 0.05};0.08;${0.25 + i * 0.05}`} dur="3s" repeatCount="indefinite" begin={`${i * 0.4}s`} />
            </circle>
          ))}
          <g>
            <line x1="100" y1="115" x2="100" y2="8" stroke="url(#radarGrad)" strokeWidth="1.5" opacity="0.75">
              <animateTransform attributeName="transform" type="rotate" from="0 100 115" to="360 100 115" dur="4s" repeatCount="indefinite" />
            </line>
          </g>
          <defs>
            <linearGradient id="radarGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#FF7A1A" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#FF7A1A" stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle cx="70" cy="70" r="4" fill="#E94545"><animate attributeName="opacity" values="1;0.5;1" dur="1.4s" repeatCount="indefinite" /></circle>
          <circle cx="150" cy="130" r="4" fill="#F4813C"><animate attributeName="opacity" values="1;0.5;1" dur="1.6s" repeatCount="indefinite" /></circle>
          <circle cx="120" cy="180" r="4" fill="#7C3AED"><animate attributeName="opacity" values="1;0.5;1" dur="1.8s" repeatCount="indefinite" /></circle>
          <circle cx="100" cy="115" r="5" fill="#FF7A1A" stroke="white" strokeWidth="1.5" />
        </svg>
        {/* Live alert toast */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
          className="absolute inset-x-2 bottom-2 rounded-xl bg-white/95 backdrop-blur border border-[#F0EBE3] p-2 flex items-center gap-2"
        >
          <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
               style={{ background: "linear-gradient(135deg, #F4813C, #E94545)" }}>
            <Thermometer className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-semibold leading-tight truncate">Caldo estremo · 39°C</div>
            <div className="text-[9px] text-[#7A7A7A] leading-tight">Evita asfalto tra 11 e 18</div>
          </div>
          <ChevronRight className="h-3.5 w-3.5 text-[#C4B79A]" />
        </motion.div>
      </div>
    </div>
  );
}

/* --- Scene 3: Match (real illustration + swipe card) --- */
function SceneMatch() {
  return (
    <div className="absolute inset-0 px-3 py-2 text-[#1A1A1A]">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-[8.5px] uppercase tracking-widest text-[#2DBA60]">Match del giorno</div>
          <div className="font-display text-[16px] leading-tight">Compagni di passeggiata</div>
        </div>
        <div className="rounded-full bg-white border border-[#F0EBE3] px-2 py-0.5 flex items-center gap-1 shrink-0">
          <Star className="h-2.5 w-2.5 text-[#7B61FF] fill-[#7B61FF]" />
          <span className="text-[8.5px] font-semibold">3</span>
        </div>
      </div>
      {/* Card stack */}
      <div className="relative h-[calc(100%-32px)]">
        <div className="absolute inset-x-4 top-4 bottom-10 rounded-2xl border border-[#F0EBE3] bg-white/60" />
        <motion.div
          initial={{ scale: 0.95, opacity: 0, rotate: -3 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="absolute inset-x-0 top-0 bottom-8 rounded-2xl overflow-hidden border border-[#E8E1CC] bg-white"
          style={{ boxShadow: "0 12px 28px -12px rgba(0,0,0,0.22)" }}
        >
          {/* Real ORIO illustration (girl + dog) via IlloImg */}
          <IlloImg
            slug="match-walking-v2"
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.65) 100%)" }} />
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-white/95 text-[9px] font-semibold inline-flex items-center gap-1">
            <MapPin className="h-2.5 w-2.5" /> 1,2 km
          </div>
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold text-white inline-flex items-center gap-1"
               style={{ background: "linear-gradient(135deg,#2DBA60,#34D399)" }}>
            <Heart className="h-2.5 w-2.5 fill-white" /> 92%
          </div>
          <div className="absolute inset-x-2 bottom-2 text-white">
            <div className="text-[8px] uppercase tracking-widest text-white/85">Compagno di passeggiata</div>
            <div className="font-display text-[16px] leading-tight mt-0.5">Sara &amp; Otto</div>
            <div className="text-[9px] text-white/95 leading-tight mt-0.5">Border · 3 anni · parco Sempione</div>
          </div>
        </motion.div>
        {/* Action row */}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-3">
          <div className="h-7 w-7 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center">
            <X className="h-3 w-3 text-[#7A7A7A]" />
          </div>
          <motion.div
            initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ delay: 0.35, type: "spring", stiffness: 260 }}
            className="h-10 w-10 rounded-full flex items-center justify-center text-white"
            style={{ background: "linear-gradient(135deg,#2DBA60,#34D399)", boxShadow: "0 6px 12px -3px rgba(45,186,96,0.55)" }}>
            <Heart className="h-4 w-4 fill-white" />
          </motion.div>
          <div className="h-7 w-7 rounded-full flex items-center justify-center text-white"
               style={{ background: "linear-gradient(135deg,#7B61FF,#5B44E0)" }}>
            <Star className="h-3 w-3 fill-white" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* --- Scene 4: Walk (Apple-Fitness rings + live stats, warm not-black card) --- */
function SceneWalk() {
  const ringR = [42, 32, 22];
  const ringVal = [0.72, 0.55, 0.40];
  const ringColor = ["#FF3B30", "#34D399", "#5B9EE0"];
  return (
    <div className="absolute inset-0 px-3 py-2 text-[#1A1A1A]">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-[8.5px] uppercase tracking-widest text-[#FF7A1A]">ORIO Walk · Live</div>
          <div className="font-display text-[16px] leading-tight">Passeggiata di Luna</div>
        </div>
        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold text-white inline-flex items-center gap-1"
              style={{ background: "linear-gradient(135deg,#2DBA60,#34D399)" }}>
          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> REC
        </span>
      </div>
      {/* Warm cream card — NOT a heavy black rectangle. Rings sit on soft cream, high contrast. */}
      <div className="relative h-[calc(100%-32px)] rounded-2xl border border-[#F0EBE3] p-3 flex flex-col items-center"
           style={{ background: "linear-gradient(180deg, #FFF8ED 0%, #FBEFD9 100%)" }}>
        {/* Concentric rings — big, centered, with total % readable in the middle */}
        <div className="relative">
          <svg viewBox="0 0 100 100" className="w-[130px] h-[130px]">
            {ringR.map((r, i) => (
              <circle key={`bg-${i}`} cx="50" cy="50" r={r} fill="none" stroke={`${ringColor[i]}22`} strokeWidth="7" />
            ))}
            {ringR.map((r, i) => (
              <motion.circle
                key={`fg-${i}`}
                cx="50" cy="50" r={r} fill="none" stroke={ringColor[i]} strokeWidth="7" strokeLinecap="round"
                transform="rotate(-90 50 50)"
                strokeDasharray={2 * Math.PI * r}
                initial={{ strokeDashoffset: 2 * Math.PI * r }}
                animate={{ strokeDashoffset: 2 * Math.PI * r * (1 - ringVal[i]) }}
                transition={{ duration: 1.4, ease: "easeOut", delay: i * 0.1 }}
              />
            ))}
          </svg>
          {/* Center readout */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="font-display text-[22px] leading-none tracking-tight" style={{ color: "#FF3B30" }}>72%</div>
            <div className="text-[8px] text-[#7A6E56] uppercase tracking-[0.14em] mt-0.5">Obiettivo</div>
          </div>
        </div>
        {/* Live stats — 3 pills, high contrast on cream */}
        <div className="grid grid-cols-3 gap-1.5 w-full mt-2">
          <StatPill color="#FF3B30" label="Distanza" value="1.8" unit="km" />
          <StatPill color="#34D399" label="Tempo"    value="24"  unit="min" />
          <StatPill color="#FFB020" label="Karma"    value="+8"  unit="" />
        </div>
      </div>
    </div>
  );
}

function StatPill({ color, label, value, unit }) {
  return (
    <div className="rounded-xl bg-white border p-1.5 text-center" style={{ borderColor: `${color}33` }}>
      <div className="text-[8px] text-[#7A6E56] uppercase tracking-widest">{label}</div>
      <div className="font-display text-[16px] leading-none mt-0.5" style={{ color }}>
        {value}{unit && <span className="text-[9px] ml-0.5 text-[#7A6E56]">{unit}</span>}
      </div>
    </div>
  );
}
